import { numFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Skeleton,
  Alert,
  AlertTitle,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Select,
  InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import AddIcon from '@mui/icons-material/Add';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HistoryIcon from '@mui/icons-material/History';
import UndoIcon from '@mui/icons-material/Undo';
import WarningIcon from '@mui/icons-material/Warning';
import PaymentReminderDialog from './PaymentReminderDialog';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PaymentsIcon from '@mui/icons-material/Payments';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BuildIcon from '@mui/icons-material/Build';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import OutboxIcon from '@mui/icons-material/Outbox';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import MaventaStatusBadge from './MaventaStatusBadge';
import AccountingTransferBadge from './AccountingTransferBadge';
import AmiliPaymentStatusBadge from './AmiliPaymentStatusBadge';
import SveaFinancingStatusBadge from './SveaFinancingStatusBadge';
import FinancingPanel from './financing/FinancingPanel';
import FinancingRefundDialog from './financing/FinancingRefundDialog';
import ResursFinancingDialog from './financing/ResursFinancingDialog';
import ResursFinancingPanel from './financing/ResursFinancingPanel';
import useFinancingStatus from './financing/useFinancingStatus';
import { can } from '../../utils/access';
import { PAYMENT_METHODS } from '../../utils/paymentMethods';
import EditInvoiceLineDialog from './EditInvoiceLineDialog';
import AddInvoiceLineDialog from './AddInvoiceLineDialog';
import EditInvoiceHeaderDialog from './EditInvoiceHeaderDialog';
import EditInvoiceInsuranceDialog from './EditInvoiceInsuranceDialog';
import InvoiceEventLogDialog from './InvoiceEventLogDialog';
import SplitPaymentDialog from './SplitPaymentDialog';
import InvoiceDeliveryTimeline from './InvoiceDeliveryTimeline';
import InvoiceAttachmentsPanel from './InvoiceAttachmentsPanel';
import CustomerDialog from '../customers/CustomerDialog';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DecimalField from '../shared/DecimalField';
import HelpButton from '../shared/HelpButton';
import AmiliActions from './AmiliActions';
import AmiliMessages from './AmiliMessages';
import {
  TERMINAL_RETRY_ATTEMPTS,
  isTransientTerminalCondition,
  terminalErrorMessage,
  waitBeforeTerminalRetry,
} from '../../utils/terminalErrors';
import { useUserStore, useSubscriptionStore } from '../../store';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';

// Mirrors MAX_MAVENTA_ATTACHMENTS_BYTES in invoicing/services/attachments.py.
// The backend is authoritative; this only lets the user learn before they click.
const MAX_MAVENTA_ATTACHMENT_BYTES = 4 * 1024 * 1024;

const dateStringToDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00');
};

const dateToDateString = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Backend error codes -> translation keys. The API also returns an English `error` string,
// used as the fallback so an unmapped code still says something useful.
const ERROR_KEYS = {
  credit_note_not_allowed: 'invoices.errorCreditNoteNotAllowed',
  split_leg_not_correctable: 'invoices.errorSplitLegNotCorrectable',
  credit_not_applicable: 'invoices.errorCreditNotApplicable',
  correction_not_allowed: 'invoices.errorCorrectionNotAllowed',
  refund_not_pending: 'invoices.errorRefundNotPending',
  copy_not_allowed: 'invoices.errorCopyNotAllowed',
  admin_only: 'invoices.errorAdminOnly',
  invoice_refunded: 'invoices.errorInvoiceRefunded',
  fully_credited: 'invoices.errorFullyCredited',
  credit_exceeds_original: 'invoices.errorCreditExceedsOriginal',
  partial_credit_unpaid: 'invoices.errorPartialCreditUnpaid',
  invalid_refund_method: 'invoices.errorInvalidRefundMethod',
  refund_method_not_applicable: 'invoices.errorRefundMethodNotApplicable',
  refund_method_unpaid_original: 'invoices.errorRefundMethodUnpaidOriginal',
  // Ohisuoritus (mark_paid_externally)
  amili_partial_not_supported: 'invoices.errorAmiliPartialNotSupported',
  amili_report_failed: 'invoices.errorAmiliReportFailed',
  exceeds_open_balance: 'invoices.errorPaymentExceedsOpenBalance',
  future_date: 'invoices.errorPaymentFutureDate',
  settled: 'invoices.errorSettled',
  // Ohisuoritus on a paid invoice Amili is still collecting (amili_conflict_at)
  amili_claim_changed: 'invoices.errorAmiliClaimChanged',
  amili_claim_mismatch: 'invoices.errorAmiliClaimMismatch',
  amili_not_collecting: 'invoices.errorAmiliNotCollecting',
  amili_conflict_report_failed: 'invoices.errorAmiliConflictReportFailed',
  // Laskutettu muualla: un-marking, reversing or crediting it is refused (undo = rollback)
  invoiced_elsewhere: 'invoices.errorInvoicedElsewhere',
};

const errorKey = (code) => ERROR_KEYS[code] || `invoices.error_${code}`;

// Amili statuses under which Amili is no longer following the invoice (mirrors
// AmiliService.UNMONITORED_STATUSES). Everything else with an assignment id is live.
const AMILI_UNMONITORED_STATUSES = ['cancelled', 'not_tracked', 'credit_loss'];
// Paid here, still collected by Amili: the ohisuoritus reports what Amili has not booked yet.
const isAmiliConflictPayable = (inv) => inv?.status === 'paid'
  && Boolean(inv?.amili_conflict_at)
  && Boolean(inv?.amili_assignment_id)
  && inv?.settlement_status !== 'credited'
  && inv?.invoice_type !== 'INV02';

// Amili's open claim from the stored events: the total less the payments Amili has booked.
// The backend re-reads it live and refuses if it no longer matches what the dialog showed.
const amiliOpenClaim = (inv) => {
  const booked = (inv?.amili_events || [])
    .filter((event) => String(event?.type || '').toLowerCase() === 'paid')
    .reduce((sum, event) => sum + (parseFloat(event?.data?.sum_paid) || 0), 0);
  const open = (parseFloat(inv?.total_incl_vat) || 0) - booked;
  return Math.max(Math.round(open * 100) / 100, 0);
};

const isAmiliMonitored = (inv) => Boolean(inv?.amili_assignment_id)
  && !AMILI_UNMONITORED_STATUSES.includes(inv?.amili_payment_status);

// Labels for Amili's assignment events. The raw type codes used to be printed as-is, so a
// "reminder_sent" or a 18,08 € "paid" booking meant nothing to the workshop.
const AMILI_EVENT_KEYS = {
  delivery_status: 'amili.event.deliveryStatus',
  reminder_sent: 'amili.event.reminderSent',
  collection_started: 'amili.event.collectionStarted',
  paid: 'amili.event.paid',
  interest_paid: 'amili.event.interestPaid',
  partially_close: 'amili.event.partiallyClosed',
  close: 'amili.event.closed',
  cancel: 'amili.event.cancelled',
  cancelled: 'amili.event.cancelled',
  recall: 'amili.event.cancelled',
  comment: 'amili.event.comment',
  collection_hold: 'amili.event.collectionHold',
  freeze: 'amili.event.collectionHold',
  freeze_confirmed: 'amili.event.freezeConfirmed',
  unfreezed: 'amili.event.unfreezed',
  second_reminder_sent: 'amili.event.secondReminderSent',
  second_demand_sent: 'amili.event.secondDemandSent',
  due_date_changed: 'amili.event.dueDateChanged',
  credit_note: 'amili.event.creditNote',
  legal_collection_started: 'amili.event.legalCollectionStarted',
  payment_plan: 'amili.event.paymentPlan',
  credit_loss: 'amili.event.creditLoss',
  tratta_sent: 'amili.event.trattaSent',
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fi-FI');
};

const formatCurrency = (amount) => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

// Format a masked PAN (e.g. "************1234") as "•••• 1234".
const formatMaskedCard = (masked) => {
  if (!masked) return '';
  const last4 = masked.replace(/\s/g, '').slice(-4);
  return `•••• ${last4}`;
};

// Format Nexi acquirer_timestamp (YYMMDDHHMMSS) as "DD.MM.YYYY HH:MM".
const formatCardTimestamp = (ts) => {
  if (!ts || ts.length < 12 || !/^\d{12}/.test(ts)) return ts || '';
  return `${ts.slice(4, 6)}.${ts.slice(2, 4)}.20${ts.slice(0, 2)} ${ts.slice(6, 8)}:${ts.slice(8, 10)}`;
};

const CARD_ENTRY_MODE_KEYS = {
  CONTACT_EMV: 'terminal.entryMode.chip',
  CONTACTLESS_EMV: 'terminal.entryMode.contactless',
  MAGNETIC_STRIPE: 'terminal.entryMode.magstripe',
  MANUAL: 'terminal.entryMode.manual',
};

const CARD_CVM_KEYS = {
  PIN: 'terminal.cvm.pin',
  SIGNATURE: 'terminal.cvm.signature',
  PIN_AND_SIGNATURE: 'terminal.cvm.pinSignature',
  CONSUMER_DEVICE: 'terminal.cvm.onDevice',
  NONE: 'terminal.cvm.none',
};

// Delivery methods routed through Maventa. Every one of them renders the buyer's postal
// address from the same Finvoice block: the bank network rejects e-invoices without it and
// Maventa cannot render the emailed/printed PDF. Sending anyway means Maventa accepts the
// document and bounces it hours later, leaving the invoice marked sent but never delivered.
const MAVENTA_DELIVERY_METHODS = ['verkkolasku', 'email_maventa', 'paper_priority', 'paper_economy'];

// Mirrors Invoice.PAYMENT_ROUNDING_TOLERANCE: an amount this close to the open balance is the whole balance.
const PAYMENT_ROUNDING_TOLERANCE = 0.05;

const InvoiceDetailView = ({ t }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const { subscriptionInfo, fetchSubscriptionInfo, canUseEinvoice } = useSubscriptionStore();
  const maventaEnabled = currentOrganization?.maventa_enabled || false;
  const einvoicingAvailable = currentOrganization?.einvoicing_available ?? true;
  const amiliEnabled = currentOrganization?.amili_enabled || false;

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [ccAccountant, setCcAccountant] = useState(false);
  const [attachPdf, setAttachPdf] = useState(false);
  const [sending, setSending] = useState(false);

  // Attachment summary for the send dialog and the Actions menu. Reported up by
  // InvoiceAttachmentsPanel, which owns the fetch — the invoice payload itself is
  // untouched so InvoiceDetailSerializer stays exactly as it was.
  const [attachmentSummary, setAttachmentSummary] = useState({ count: 0, totalBytes: 0 });
  // The panel hides itself when there are no attachments, so "Lisää liite" in the
  // Actions menu is the entry point for the first one.
  const attachmentsRef = useRef(null);

  // Invoice editing dialogs state
  const [editLineDialogOpen, setEditLineDialogOpen] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [addLineDialogOpen, setAddLineDialogOpen] = useState(false);
  const [editHeaderDialogOpen, setEditHeaderDialogOpen] = useState(false);
  const [insuranceDialogOpen, setInsuranceDialogOpen] = useState(false);
  const [editHeaderFocusField, setEditHeaderFocusField] = useState(null);

  // Edit-customer-from-invoice state (reflects address fixes back onto the invoice)
  const [editCustomerDialogOpen, setEditCustomerDialogOpen] = useState(false);
  const [editCustomerData, setEditCustomerData] = useState(null);

  // Actions menu state
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);

  // Apply discount dialog state
  const [applyDiscountDialogOpen, setApplyDiscountDialogOpen] = useState(false);
  const [discountPercent, setDiscountPercent] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  // Round total dialog state
  const [roundTotalDialogOpen, setRoundTotalDialogOpen] = useState(false);
  const [desiredTotal, setDesiredTotal] = useState('');
  const [roundingTotal, setRoundingTotal] = useState(false);

  // Event log dialog state
  const [eventLogDialogOpen, setEventLogDialogOpen] = useState(false);

  // Cash/card payment state
  const [markingCashPayment, setMarkingCashPayment] = useState(false);

  // Svea financing state
  const sveaFinancingEnabled = currentOrganization?.svea_financing_enabled || false;
  const [sveaLoading, setSveaLoading] = useState(false);
  const [sveaDialogOpen, setSveaDialogOpen] = useState(false);
  const [sveaSending, setSveaSending] = useState(null);

  // Svea S2S (instant credit decision) state
  const [sveaS2sDialogOpen, setSveaS2sDialogOpen] = useState(false);
  const [sveaS2sLoading, setSveaS2sLoading] = useState(false);
  const [sveaS2sResult, setSveaS2sResult] = useState(null);
  const [sveaS2sPaymentMethod, setSveaS2sPaymentMethod] = useState('FI70');
  const [sveaS2sHetu, setSveaS2sHetu] = useState('');
  const [sveaS2sPlans, setSveaS2sPlans] = useState([]);
  const [sveaS2sSelectedPlan, setSveaS2sSelectedPlan] = useState(null);
  const [sveaS2sPlansLoading, setSveaS2sPlansLoading] = useState(false);

  const sveaMissingFields = useMemo(() => {
    if (!invoice) return [];
    const missing = [];
    if (!invoice.customer_name) missing.push(t('svea.missingName', 'nimi'));
    if (!invoice.customer_email) missing.push(t('svea.missingEmail', 'sähköposti'));
    if (!invoice.customer_address_street && !invoice.customer_address) missing.push(t('svea.missingAddress', 'osoite'));
    if (!invoice.customer_address_postal_code) missing.push(t('svea.missingPostalCode', 'postinumero'));
    if (!invoice.customer_address_city) missing.push(t('svea.missingCity', 'kaupunki'));
    return missing;
  }, [invoice, t]);
  const sveaDisabled = sveaMissingFields.length > 0;
  const sveaTooltip = sveaDisabled
    ? `${t('svea.missingFieldsTooltip', 'Täytä asiakkaalle seuraavat tiedot:')} ${sveaMissingFields.join(', ')}`
    : '';

  // Svea refund state
  const [sveaRefundDialogOpen, setSveaRefundDialogOpen] = useState(false);
  const [sveaRefundLoading, setSveaRefundLoading] = useState(false);
  const [sveaBankTransfer, setSveaBankTransfer] = useState(null);
  const [financingCancelLoading, setFinancingCancelLoading] = useState(false);

  // Resurs financing state (financing/ResursFinancingDialog.js)
  const resursFinancingEnabled = currentOrganization?.resurs_financing_enabled || false;
  const [resursDialogOpen, setResursDialogOpen] = useState(false);
  const [resursRefundDialogOpen, setResursRefundDialogOpen] = useState(false);
  const [resursRefundLoading, setResursRefundLoading] = useState(false);

  // Terminal payment state
  const paymentTerminalEnabled = currentOrganization?.payment_terminal_enabled || false;
  const [terminals, setTerminals] = useState([]);
  // False until the terminal list has been fetched (or is known not to be needed), so an
  // automatic card payment never falls back to a manual booking just because it ran early.
  const [terminalsLoaded, setTerminalsLoaded] = useState(!paymentTerminalEnabled);
  const [terminalPaymentLoading, setTerminalPaymentLoading] = useState(false);
  const [terminalSelectOpen, setTerminalSelectOpen] = useState(false);

  // Terminal refund state
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundCustomerNotPresent, setRefundCustomerNotPresent] = useState(false);
  // {index, total} while a multi-tender refund is running, so the button says how far it is.
  const [refundProgress, setRefundProgress] = useState(null);
  const [refundTerminalSelectOpen, setRefundTerminalSelectOpen] = useState(false);

  // Copy as draft state
  const [copyingAsDraft, setCopyingAsDraft] = useState(false);

  // Mark as sent dialog state
  const [markAsSentDialogOpen, setMarkAsSentDialogOpen] = useState(false);
  const [markingAsSent, setMarkingAsSent] = useState(false);

  // Verkkolasku state
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [verkkolaskuAddress, setVerkkolaskuAddress] = useState('');
  const [verkkolaskuOperator, setVerkkolaskuOperator] = useState('');
  const [insuranceClaimNumber, setInsuranceClaimNumber] = useState('');
  const [insuranceDamageDate, setInsuranceDamageDate] = useState('');
  const [lookingUpAddress, setLookingUpAddress] = useState(false);
  const [saveToCustomer, setSaveToCustomer] = useState(false);
  const [editingVerkkolaskuAddress, setEditingVerkkolaskuAddress] = useState(false);

  // Amili email state (Amili routes email through Maventa, separate from SMTP email)
  const [amiliSendEmail, setAmiliSendEmail] = useState('');

  // Mark as paid dialog state
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [markPaidDate, setMarkPaidDate] = useState('');
  // The amount in the dialog is prefilled with the open balance: left as is, the invoice is
  // marked paid; lowered, the amount is recorded as an osasuoritus and the invoice stays open.
  const [markPaidAmount, setMarkPaidAmount] = useState('');
  const [markPaidMethod, setMarkPaidMethod] = useState('bank_transfer');
  const [markPaidError, setMarkPaidError] = useState('');
  const [submittingMarkPaid, setSubmittingMarkPaid] = useState(false);

  // Credit / correction / settlement dialog state
  const [correctDialogOpen, setCorrectDialogOpen] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [applyingCredit, setApplyingCredit] = useState(false);
  const [markRefundedDialogOpen, setMarkRefundedDialogOpen] = useState(false);
  const [applyCreditDialogOpen, setApplyCreditDialogOpen] = useState(false);
  // "Palauta osat varastoon": an opt-in per credit, reset whenever a credit dialog opens.
  const [restockParts, setRestockParts] = useState(false);
  // How the money actually went back. Defaults to the original's method (the usual case), and
  // "refund_bank" keeps a bank-paid refund out of the card/cash totals, which the workshop
  // reconciles against its payment terminal's own report.
  const [refundMethod, setRefundMethod] = useState('card');
  const [unmarkPaidDialogOpen, setUnmarkPaidDialogOpen] = useState(false);
  const [unmarkPaidReason, setUnmarkPaidReason] = useState('');
  const [unmarkingPaid, setUnmarkingPaid] = useState(false);

  // Manual financing (Rahoitus) payment dialog state
  const [financingDialogOpen, setFinancingDialogOpen] = useState(false);
  const [financingReference, setFinancingReference] = useState('');
  const [markingFinancing, setMarkingFinancing] = useState(false);

  // "Laskutettu muualla": billed through another system (e.g. a chain's care-service billing)
  const [invoicedElsewhereDialogOpen, setInvoicedElsewhereDialogOpen] = useState(false);
  const [invoicedElsewhereReference, setInvoicedElsewhereReference] = useState('');
  const [invoicedElsewhereDate, setInvoicedElsewhereDate] = useState('');
  const [markingInvoicedElsewhere, setMarkingInvoicedElsewhere] = useState(false);

  // Ohisuoritus (external payment) state
  const [ohisuoritusDialogOpen, setOhisuoritusDialogOpen] = useState(false);
  const [ohisuoritusConflict, setOhisuoritusConflict] = useState(false);
  const [ohisuoritusAmount, setOhisuoritusAmount] = useState('');
  const [ohisuoritusDate, setOhisuoritusDate] = useState('');
  const [ohisuoritusError, setOhisuoritusError] = useState('');
  const [submittingOhisuoritus, setSubmittingOhisuoritus] = useState(false);

  // Reversing a recorded payment
  const [reversePaymentTarget, setReversePaymentTarget] = useState(null);
  const [reversingPayment, setReversingPayment] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Send receipt state
  const [sendReceiptDialogOpen, setSendReceiptDialogOpen] = useState(false);
  const [sendReceiptEmail, setSendReceiptEmail] = useState('');
  const [sendingReceipt, setSendingReceipt] = useState(false);

  // Combined receipt state
  const [sendCombinedReceiptDialogOpen, setSendCombinedReceiptDialogOpen] = useState(false);
  const [sendCombinedReceiptEmail, setSendCombinedReceiptEmail] = useState('');
  const [sendingCombinedReceipt, setSendingCombinedReceipt] = useState(false);

  // Split payment dialog state
  const [splitPaymentDialogOpen, setSplitPaymentDialogOpen] = useState(false);

  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Accounting integration (Netvisor etc.) transfer retry
  const [retryingAccountingTransfer, setRetryingAccountingTransfer] = useState(false);

  // Line reordering. Off by default so the row layout is unchanged for everyone
  // who does not need it — turning it on reveals up/down arrows per row.
  const [reorderMode, setReorderMode] = useState(false);
  const [reordering, setReordering] = useState(false);

  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState(null); // 'cancel' or 'rollback'
  const [confirmDialogLoading, setConfirmDialogLoading] = useState(false);

  // Check if e-invoicing is available (not available during trial or if not configured)
  const einvoiceEnabledBySubscription = canUseEinvoice();
  const einvoiceEnabled = einvoiceEnabledBySubscription && einvoicingAvailable;
  const isTrial = subscriptionInfo?.is_trial;

  // Amili active check (used throughout the unified send dialog).
  // Customers explicitly excluded from Amili are sent via the normal (non-Amili) path, since
  // Maventa does not route their invoices through Amili payment monitoring.
  const isAmili =
    amiliEnabled &&
    currentOrganization?.amili_status === 'active' &&
    !invoice?.customer_amili_excluded;

  // Maksumuistutus: offered only where reminder_eligibility() on the backend would allow
  // it. The Amili test is the organization-level one on purpose (an org on Amili has no
  // in-house reminders at all), not the per-customer exclusion used for send routing.
  const canSendPaymentReminder =
    !!currentOrganization?.payment_reminders_enabled &&
    !(amiliEnabled && currentOrganization?.amili_status === 'active') &&
    invoice?.customer_type === 'company' &&
    !!invoice?.is_overdue &&
    !invoice?.settlement_status &&
    invoice?.invoice_type !== 'INV02' &&
    (parseFloat(invoice?.open_balance ?? invoice?.total_incl_vat ?? 0) || 0) > 0;

  // Check if customer has verkkolasku address already set
  const customerHasVerkkolaskuAddress = invoice?.customer_verkkolasku_address && invoice?.customer_verkkolasku_operator;

  // Validate e-invoice address format: OVT (0037...), IBAN, or EDI/proprietary
  const isValidEinvoiceAddress = (address) => {
    if (!address) return true; // Empty is valid (will be caught by required check)
    if (/^0037[A-Z0-9]{8,13}$/i.test(address)) return true;       // OVT
    if (/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4,30}$/i.test(address)) return true; // IBAN
    if (/^[A-Z0-9]{5,30}$/i.test(address)) return true;           // EDI/other
    return false;
  };

  // Validate operator code format (4-15 alphanumeric characters: BIC, OVT operator, or other)
  const isValidOperatorCode = (code) => {
    if (!code) return true; // Empty is valid (will be caught by required check)
    return /^[A-Z0-9]{4,15}$/.test(code.toUpperCase());
  };

  // Validation errors for display
  const einvoiceAddressError = verkkolaskuAddress && !isValidEinvoiceAddress(verkkolaskuAddress);
  const operatorCodeError = verkkolaskuOperator && !isValidOperatorCode(verkkolaskuOperator);

  useEffect(() => {
    fetchInvoice();
    // Fetch subscription info if not already loaded
    if (!subscriptionInfo) {
      fetchSubscriptionInfo();
    }
  }, [id]);

  // Fetch payment terminals when feature is enabled
  useEffect(() => {
    if (paymentTerminalEnabled) {
      setTerminalsLoaded(false);
      axios.get('/api/payment-terminals/')
        .then(res => setTerminals((res.data.results || res.data).filter(t => t.is_active)))
        .catch(() => {})
        .finally(() => setTerminalsLoaded(true));
    } else {
      setTerminalsLoaded(true);
    }
  }, [paymentTerminalEnabled]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/invoices/${id}/`);
      setInvoice(response.data);
      setSendEmail(response.data.customer_email || '');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Re-read the invoice without the full-page spinner (financing status polling).
  const refreshInvoiceQuietly = async () => {
    try {
      const response = await axios.get(`/api/invoices/${id}/`);
      setInvoice(response.data);
    } catch (err) {
      // Keep showing the last state; the next poll tries again.
    }
  };

  // While a financing request waits for the customer, keep the view current so the
  // workshop sees the payment arrive without reloading. The Resurs dialog polls faster itself.
  useFinancingStatus(id, Boolean(invoice?.financing_pending) && !resursDialogOpen, (data) => {
    const changed =
      !data.financing_pending
      || data.status !== invoice?.status
      || data.resurs_payment?.status !== invoice?.resurs_payment?.status
      || data.svea_payment?.status !== invoice?.svea_payment?.status;
    if (changed) refreshInvoiceQuietly();
  });

  const handlePreviewPDF = () => {
    window.open(`/api/invoices/${id}/preview/`, '_blank');
  };

  const handleDownloadPDF = () => {
    window.open(`/api/invoices/${id}/pdf/`, '_blank');
  };

  const handleSendReceipt = async () => {
    try {
      setSendingReceipt(true);
      await axios.post(`/api/invoices/${id}/send_receipt/`, {
        email: sendReceiptEmail || undefined,
      });
      setSnackbar({
        open: true,
        message: t('Receipt sent successfully'),
        severity: 'success',
      });
      setSendReceiptDialogOpen(false);
      fetchInvoice();
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('Failed to send receipt');
      setSendReceiptDialogOpen(false);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setSendingReceipt(false);
    }
  };

  const handleDownloadCombinedReceipt = () => {
    window.open(`/api/invoices/${id}/combined-receipt-pdf/`, '_blank');
  };

  const handleSendCombinedReceipt = async () => {
    try {
      setSendingCombinedReceipt(true);
      await axios.post(`/api/invoices/${id}/send-combined-receipt/`, {
        email: sendCombinedReceiptEmail || undefined,
      });
      setSnackbar({
        open: true,
        message: t('Combined receipt sent successfully'),
        severity: 'success',
      });
      setSendCombinedReceiptDialogOpen(false);
      fetchInvoice();
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('Failed to send combined receipt');
      setSendCombinedReceiptDialogOpen(false);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setSendingCombinedReceipt(false);
    }
  };

  // True when this send will also apply the credit (draft credit note). Drives the dialog
  // wording so the button, the title and the confirm all name the same single action.
  const isCreditingDraft = invoice?.invoice_type === 'INV02'
    && invoice?.status === 'draft'
    && !invoice?.settlement_status;

  const handleSendUnified = async () => {
    // A normal DRAFT with a negative total cannot be sent (make a credit note
    // instead) — mirror the backend guard so the user learns before the request.
    // Resends of already-sent legacy invoices are not re-checked (backend policy).
    if (invoice?.status === 'draft' && invoice?.invoice_type !== 'INV02'
        && parseFloat(invoice?.total_incl_vat) < 0) {
      setSnackbar({ open: true, message: t('invoices.negativeTotalSendError'), severity: 'error' });
      return;
    }
    try {
      setSending(true);

      // A draft credit note is credited as part of sending it — one action, one dialog.
      // The credit is applied BEFORE delivery on purpose: if the send then fails, the books
      // are still right and the user just retries delivery. Backing out of this dialog
      // instead leaves a clean draft with nothing half-done.
      if (isCreditingDraft) {
        const creditResponse = await axios.post(
          `/api/invoices/${id}/apply_credit/`,
          restockParts && (invoice?.restockable_lines || []).length > 0 ? { restock_parts: true } : {},
        );
        warnIfAmiliCancelFailed(creditResponse.data.amili_cancel);
        notifyRestock(creditResponse.data.restock);
        if (creditResponse.data.credit_outcome === 'refund_pending') {
          setSnackbar({
            open: true,
            message: t('invoices.creditAppliedRefundPending'),
            severity: 'info',
          });
        }
      }

      if (isAmili) {
        // ALL Amili sends go through send_amili endpoint
        const payload = {
          delivery_method: deliveryMethod,
          save_to_customer: saveToCustomer,
        };
        if (deliveryMethod === 'verkkolasku') {
          payload.recipient_address = verkkolaskuAddress;
          payload.recipient_operator = verkkolaskuOperator;
        } else if (deliveryMethod === 'email_maventa') {
          payload.recipient_email = amiliSendEmail;
        }
        if (insuranceClaimNumber) payload.insurance_claim_number = insuranceClaimNumber;
        if (insuranceDamageDate) payload.insurance_damage_date = insuranceDamageDate;

        await axios.post(`/api/invoices/${id}/send_amili/`, payload);
        setSnackbar({
          open: true,
          message: t('amili.invoiceSentSuccessUpdated', 'Lasku lähetetty. Maksuvalvonnan tila päivittyy automaattisesti.'),
          severity: 'success',
        });
      } else if (deliveryMethod === 'email') {
        // Non-Amili email: existing SMTP endpoint
        await axios.post(`/api/invoices/${id}/send/`, {
          email: sendEmail,
          cc_accountant: ccAccountant,
          attach_pdf: attachPdf,
        });
        setSnackbar({
          open: true,
          message: t('Invoice sent successfully'),
          severity: 'success',
        });
      } else if (deliveryMethod === 'verkkolasku') {
        // Non-Amili verkkolasku: existing endpoint
        await axios.post(`/api/invoices/${id}/send_verkkolasku/`, {
          recipient_address: verkkolaskuAddress,
          recipient_operator: verkkolaskuOperator,
          insurance_claim_number: insuranceClaimNumber || undefined,
          insurance_damage_date: insuranceDamageDate || undefined,
          save_to_customer: saveToCustomer,
        });
        setSnackbar({
          open: true,
          message: t('E-invoice sent successfully'),
          severity: 'success',
        });
      } else if (deliveryMethod === 'paper_priority' || deliveryMethod === 'paper_economy') {
        // Non-Amili paper: NEW endpoint
        await axios.post(`/api/invoices/${id}/send_maventa/`, {
          delivery_method: deliveryMethod,
          insurance_claim_number: insuranceClaimNumber || undefined,
          insurance_damage_date: insuranceDamageDate || undefined,
        });
        setSnackbar({
          open: true,
          message: t('paperInvoiceSentSuccess', 'Paperilasku lähetetty onnistuneesti.'),
          severity: 'success',
        });
      }

      setSendDialogOpen(false);
      fetchInvoice();
    } catch (err) {
      const errorMessage = err.response?.data?.error;
      const errorCode = err.response?.data?.error_code;
      // The invoice was already submitted to Maventa and is awaiting delivery — we refreshed
      // its status instead of creating a duplicate. Show it as info, not an error.
      if (errorCode === 'already_submitted') {
        setSendDialogOpen(false);
        setSnackbar({
          open: true,
          message: t('invoice.alreadySubmittedRefreshed', 'Lasku on jo lähetetty Maventaan ja odottaa toimitusta. Tila päivitettiin uudelleenlähetyksen sijaan.'),
          severity: 'info',
        });
        fetchInvoice();
        return;
      }
      let translatedError;
      if (errorCode === 'delivered_use_credit_note') {
        translatedError = t('invoice.alreadyDeliveredUseCreditNote', 'Tämä verkkolasku on jo toimitettu. Tee hyvityslasku uudelleenlähetyksen sijaan.');
      } else if (errorMessage) {
        if (errorMessage.includes('not enabled')) {
          translatedError = t('E-invoicing is not enabled for this organization');
        } else if (errorMessage.includes('current status')) {
          translatedError = t('Invoice cannot be sent in current status');
        } else if (errorMessage.includes('not configured')) {
          translatedError = t('E-invoicing is not configured. Please check settings.');
        } else {
          translatedError = errorMessage;
        }
      } else {
        translatedError = t('Failed to send invoice');
      }
      setSendDialogOpen(false);
      setSnackbar({
        open: true,
        message: translatedError,
        severity: 'error',
      });
    } finally {
      setSending(false);
    }
  };

  const handleLookupVerkkolaskuAddress = async () => {
    if (!invoice?.customer_business_id) {
      setSnackbar({
        open: true,
        message: t('Customer business ID is required for e-invoice lookup'),
        severity: 'warning',
      });
      return;
    }

    setLookingUpAddress(true);
    try {
      const response = await axios.get('/api/verkkolasku/lookup/', {
        params: { business_id: invoice.customer_business_id },
      });

      if (response.data.success && response.data.addresses?.length > 0) {
        const firstAddress = response.data.addresses[0];
        setVerkkolaskuAddress(firstAddress.address || '');
        setVerkkolaskuOperator(firstAddress.operator || '');
        setSnackbar({
          open: true,
          message: t('E-invoice address found'),
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: t('No e-invoice address found for this business ID'),
          severity: 'warning',
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('Failed to lookup e-invoice address'),
        severity: 'error',
      });
    } finally {
      setLookingUpAddress(false);
    }
  };

  const openSendDialog = () => {
    // Reset dialog state
    setSendEmail(invoice?.customer_email || '');
    setCcAccountant(false);
    setAttachPdf(false);
    setRestockParts(false);
    setAmiliSendEmail(invoice?.customer_email || '');
    // Pre-populate verkkolasku address:
    // If previous delivery failed, prefer customer's current (possibly corrected) values.
    // Otherwise, prefer the values from the previous successful send.
    const maventaFailed = invoice?.maventa_status === 'failed';
    setVerkkolaskuAddress(
      maventaFailed
        ? (invoice?.customer_verkkolasku_address || invoice?.recipient_verkkolasku_address || '')
        : (invoice?.recipient_verkkolasku_address || invoice?.customer_verkkolasku_address || '')
    );
    setVerkkolaskuOperator(
      maventaFailed
        ? (invoice?.customer_verkkolasku_operator || invoice?.recipient_verkkolasku_operator || '')
        : (invoice?.recipient_verkkolasku_operator || invoice?.customer_verkkolasku_operator || '')
    );
    setInsuranceClaimNumber(invoice?.insurance_claim_number || '');
    setInsuranceDamageDate(invoice?.insurance_damage_date || '');
    // Default to saving to customer if customer doesn't have address set yet
    setSaveToCustomer(!invoice?.customer_verkkolasku_address);
    setEditingVerkkolaskuAddress(false);
    // Smart default: company customer with einvoice -> verkkolasku, otherwise email
    if (isAmili) {
      setDeliveryMethod(invoice?.customer_type === 'company' ? 'verkkolasku' : 'email_maventa');
    } else {
      setDeliveryMethod(
        invoice?.customer_type === 'company' && maventaEnabled && einvoiceEnabled ? 'verkkolasku' : 'email'
      );
    }
    setSendDialogOpen(true);
  };

  // Open the customer edit dialog for the invoice's linked customer.
  const openEditCustomerDialog = async () => {
    if (!invoice?.customer_id) {
      setSnackbar({
        open: true,
        message: t('invoices.noLinkedCustomer', 'Laskulla ei ole liitettyä asiakasta'),
        severity: 'warning',
      });
      return;
    }
    try {
      const response = await axios.get(`/api/customers/${invoice.customer_id}/`);
      setEditCustomerData(response.data);
      setEditCustomerDialogOpen(true);
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('invoices.customerLoadFailed', 'Asiakkaan tietojen lataus epäonnistui'),
        severity: 'error',
      });
    }
  };

  // Called after the customer is saved in the dialog; reflects the corrected data
  // back onto the invoice snapshot (allowed for draft / failed e-invoices).
  const handleCustomerSaved = async (updatedCustomer) => {
    setEditCustomerData(updatedCustomer);
    try {
      const response = await axios.post(`/api/invoices/${id}/refresh-customer-data/`);
      setInvoice(response.data);
      setSnackbar({
        open: true,
        message: t('invoices.customerInfoUpdated', 'Asiakkaan tiedot päivitetty laskulle'),
        severity: 'success',
      });
    } catch (err) {
      // The customer's own data was saved, but the invoice snapshot could not be updated
      // (a delivered or paid invoice is an immutable document). Never claim success here —
      // the whole point of this action is that the invoice picks up the corrected data.
      fetchInvoice();
      setSnackbar({
        open: true,
        message: t(
          'invoices.customerInfoNotUpdatedOnInvoice',
          'Asiakkaan tiedot tallennettiin, mutta niitä ei voitu päivittää tälle laskulle. Lasku on jo toimitettu tai maksettu.'
        ),
        severity: 'warning',
      });
    }
  };

  const handleRetryAccountingTransfer = async (transferId) => {
    setRetryingAccountingTransfer(true);
    try {
      await axios.post(`/api/accounting-integrations/transfers/${transferId}/retry/`);
      setSnackbar({
        open: true,
        message: t('accounting.retryQueued', 'Siirto lisätty jonoon uudelleen'),
        severity: 'success',
      });
      fetchInvoice();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('accounting.retryFailed', 'Siirron uudelleenyritys epäonnistui'),
        severity: 'error',
      });
    } finally {
      setRetryingAccountingTransfer(false);
    }
  };

  // The open balance the dialog measures the typed amount against, and whether that amount
  // covers it (within Invoice.PAYMENT_ROUNDING_TOLERANCE, the Finnish cash rounding unit).
  const markPaidOpenBalance = parseFloat(invoice?.open_balance ?? invoice?.total_incl_vat ?? 0) || 0;
  const markPaidAmountValue = parseFloat(markPaidAmount);
  const markPaidCovers = !Number.isNaN(markPaidAmountValue)
    && markPaidAmountValue >= markPaidOpenBalance - PAYMENT_ROUNDING_TOLERANCE;
  // An osasuoritus is a bank transfer only for now (cash and card partials are coming), so
  // below the open balance the method is fixed; a full payment keeps the invoice's own method.
  const markPaidIsPartial = !markPaidCovers && markPaidAmountValue > 0;
  const markPaidEffectiveMethod = markPaidIsPartial ? 'bank_transfer' : markPaidMethod;

  const openMarkPaidDialog = () => {
    setMarkPaidDate(dateToDateString(new Date()));
    setMarkPaidAmount(String(invoice?.open_balance ?? invoice?.total_incl_vat ?? ''));
    setMarkPaidMethod(invoice?.payment_method || 'bank_transfer');
    setMarkPaidError('');
    setMarkPaidDialogOpen(true);
  };

  // One dialog for both: the full open balance goes through mark_paid exactly as before,
  // anything smaller is an osasuoritus through record_payment. Over the balance and zero
  // are refused here, with the same messages the ledger would give.
  const handleMarkPaid = async () => {
    if (Number.isNaN(markPaidAmountValue) || (!markPaidCovers && markPaidAmountValue <= 0)) {
      setMarkPaidError(t('invoices.errorPaymentZero'));
      return;
    }
    if (markPaidAmountValue > markPaidOpenBalance + PAYMENT_ROUNDING_TOLERANCE) {
      setMarkPaidError(t('invoices.errorPaymentExceedsOpenBalance'));
      return;
    }
    setMarkPaidError('');
    setSubmittingMarkPaid(true);
    try {
      if (markPaidCovers) {
        await axios.post(`/api/invoices/${id}/mark_paid/`, {
          paid_date: markPaidDate || undefined,
        });
      } else {
        await axios.post(`/api/invoices/${id}/record_payment/`, {
          amount: markPaidAmount,
          payment_date: markPaidDate,
          payment_method: markPaidEffectiveMethod,
        });
        setSnackbar({ open: true, message: t('invoices.paymentRecorded'), severity: 'success' });
      }
      setMarkPaidDialogOpen(false);
      fetchInvoice();
    } catch (err) {
      setError(err.response?.data?.error || t('Failed to mark as paid'));
    } finally {
      setSubmittingMarkPaid(false);
    }
  };

  const handleCancel = () => {
    setConfirmDialogAction('cancel');
    setConfirmDialogOpen(true);
  };

  const handleRollbackToDraft = () => {
    setConfirmDialogAction('rollback');
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
    setConfirmDialogAction(null);
  };

  const handleConfirmDialogConfirm = async () => {
    setConfirmDialogLoading(true);
    try {
      if (confirmDialogAction === 'cancel') {
        await axios.post(`/api/invoices/${id}/cancel/`);
        fetchInvoice();
        setSnackbar({
          open: true,
          message: t('Invoice cancelled'),
          severity: 'success',
        });
      } else if (confirmDialogAction === 'rollback') {
        const response = await axios.post(`/api/invoices/${id}/rollback_to_draft/`);
        setInvoice(response.data);
        setSnackbar({
          open: true,
          message: t('Invoice rolled back to draft'),
          severity: 'success',
        });
      }
      handleConfirmDialogClose();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('Operation failed'),
        severity: 'error',
      });
      setConfirmDialogLoading(false);
    } finally {
      setConfirmDialogLoading(false);
    }
  };

  // Helper: check if payment is cash or card
  const isCashOrCard = invoice?.payment_method === 'cash' || invoice?.payment_method === 'card';

  // Credited part lines whose stock can go back on the shelf. The backend lists only parts
  // that are tracked and still deducted, so an empty list means the choice is not offered.
  const restockableLines = invoice?.restockable_lines || [];

  // The refund route is only asked for a draft cash/card credit note; a credit note that was
  // already delivered (issued before settlement existed) is just settled.
  const asksRefundMethod = invoice?.status === 'draft' && isCashOrCard;

  // Feedback for "Palauta osat varastoon". A partial failure must not hide behind the
  // success toast: the user has to go and check the balances by hand.
  const notifyRestock = (restock) => {
    if (!restock?.requested || !restock.errors?.length) return false;
    setSnackbar({ open: true, message: t('invoices.restockPartialFailure'), severity: 'warning' });
    return true;
  };

  // Lists exactly what goes back, so a goodwill credit (parts stay in the car) is not
  // restocked by a reflex tick. Shared by the apply-credit and the send dialog.
  const renderRestockChoice = (sx = { mt: 2 }) => restockableLines.length > 0 && (
    <Box sx={sx} data-testid="restock-parts-section">
      <FormControlLabel
        control={(
          <Checkbox
            size="small"
            checked={restockParts}
            onChange={(e) => setRestockParts(e.target.checked)}
            inputProps={{ 'data-testid': 'restock-parts-checkbox' }}
          />
        )}
        label={t('invoices.restockPartsLabel')}
      />
      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
        {t('invoices.restockPartsHelp')}
      </Typography>
      <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 6 }} data-testid="restock-parts-list">
        {restockableLines.map((line) => (
          <Typography component="li" variant="body2" key={line.line_id}>
            {line.part_name}: {line.quantity} {line.unit}
          </Typography>
        ))}
      </Box>
    </Box>
  );

  // Check if invoice can be rolled back to draft (non-verkkolasku, sent or paid cash/card)
  // Invoiced elsewhere: never delivered from here, so the draft's delivery method does not matter
  const canRollbackToDraft = (
    invoice?.status === 'sent' ||
    (invoice?.status === 'paid' && (isCashOrCard || invoice?.payment_method === 'invoiced_elsewhere'))
  ) &&
    !invoice?.maventa_invoice_id &&
    (invoice?.delivery_method !== 'verkkolasku' || invoice?.payment_method === 'invoiced_elsewhere');

  // Check if invoice can be cancelled (extended for cash/card/email invoices)
  const canCancel = invoice?.status === 'draft' ||
    ((invoice?.status === 'sent' || invoice?.status === 'paid') &&
     !invoice?.maventa_invoice_id &&
     invoice?.delivery_method !== 'verkkolasku' &&
     (isCashOrCard || invoice?.delivery_method === 'email' || invoice?.delivery_method === 'pdf' || invoice?.delivery_method === 'manual')) ||
    // Failed (never-delivered) Maventa invoice: allow cancelling so the work order can be
    // re-invoiced. Any Maventa route can bounce, not just verkkolasku — matches the
    // backend condition in InvoiceService.cancel_invoice.
    (invoice?.status === 'sent' && invoice?.maventa_status === 'failed');

  // A Maventa send is blocked up front when the recipient's postal address is incomplete;
  // the backend refuses it too (MaventaService.validate_recipient_address).
  const recipientAddressIncomplete = !invoice?.customer_address_street
    || !invoice?.customer_address_postal_code
    || !invoice?.customer_address_city;
  const deliveryNeedsPostalAddress = MAVENTA_DELIVERY_METHODS.includes(deliveryMethod);

  // A draft goes out in the buyer's name. A customer saved with only a phone number or email
  // gets a name first; the draft follows the customer. Every delivery route is blocked, and
  // the backend refuses it too (invoice_service.missing_buyer_name_error). Sent invoices are
  // not re-checked, so a resend stays possible.
  const buyerNameMissing = invoice?.status === 'draft' && !invoice?.customer_name?.trim();
  const buyerNameWarning = buyerNameMissing && (
    <Alert severity="warning" sx={{ mb: 2 }} data-testid="invoice-buyer-name-missing">
      {t('invoices.buyerNameMissing')}
      {invoice?.customer_id && (
        <Button
          size="small"
          sx={{ ml: 1 }}
          onClick={() => {
            setSendDialogOpen(false);
            setMarkAsSentDialogOpen(false);
            openEditCustomerDialog();
          }}
        >
          {t('invoices.editCustomerInfo', 'Muokkaa asiakkaan tietoja')}
        </Button>
      )}
    </Alert>
  );

  // Our own IBAN/BIC ride along in the e-invoice payment block and the recipient's bank
  // checks them. A missing or contradicting BIC is rejected hours after the invoice looks
  // delivered, so the send is blocked here and in the backend (validate_sender_bank_details).
  // The backend leaves the field null for Amili-routed invoices, which carry Amili's details.
  const senderBankError = invoice?.sender_bank_error || null;
  const senderBankBlocksEinvoice = deliveryMethod === 'verkkolasku' && !!senderBankError;

  // Actions menu section visibility — used to insert dividers only between non-empty groups
  const isDraftInvoice = invoice?.status === 'draft';
  const showPdfActions = !(amiliEnabled && !invoice?.maventa_invoice_id && isDraftInvoice);
  const showEditActions = isDraftInvoice || (invoice?.status === 'sent' && invoice?.maventa_status === 'failed');
  const showManualFinancing = isDraftInvoice && invoice?.invoice_type !== 'INV02' && !invoice?.amili_assignment_id;
  // Billed through another system: corrected there, and undone here by rolling back to draft
  const isInvoicedElsewhere = invoice?.payment_method === 'invoiced_elsewhere';
  // Paid with several tenders: two cards, or cash and a card, on one invoice.
  const hasTenderLegs = (invoice?.payment_entries?.length || 0) > 0;
  // Reversing such a sale runs leg by leg: each card goes back on the reader for its own
  // amount and a cash leg is handed over the counter. Offered while at least one card was
  // captured on our terminal; with no capture there is nothing for the reader to do and
  // the credit note (or cancel) is the instrument.
  const unrefundedLegs = (invoice?.payment_entries || []).filter((leg) => !leg.refunded_at);
  const hasCapturedCardLeg = (invoice?.payment_entries || []).some((leg) => leg.terminal_transaction);
  const cashToReturnOnRefund = unrefundedLegs
    .filter((leg) => !leg.terminal_transaction)
    .reduce((sum, leg) => sum + (parseFloat(leg.amount) || 0), 0);
  const canRefundOnTerminal = hasTenderLegs
    ? unrefundedLegs.length > 0 && hasCapturedCardLeg
    : invoice?.payment_method === 'card';
  // Cash/card legs of a work-order split carry a single synthetic "Ennakkomaksu" line
  // instead of the real work rows, so "Korjaa lasku" on one produces a meaningless draft.
  const isImmediateSplitChild = ['payment_cash', 'payment_card'].includes(invoice?.invoice_split_type);

  const showPaymentActions =
    isDraftInvoice ||
    (!['draft', 'cancelled', 'combined'].includes(invoice?.status) &&
      invoice?.invoice_type !== 'INV02' &&
      !isCashOrCard);

  const paymentMethodLabels = Object.fromEntries(PAYMENT_METHODS.map((m) => [m.value, t(m.labelKey)]));

  // Tender legs of a split payment (part cash, part card at the counter). Empty for every
  // other invoice, where payment_method is the whole story. With legs, payment_method only
  // holds the primary leg, so the legs are what the screen must show — the receipt PDF
  // already prints the same maksutapaerittely.
  const paymentLegs = invoice?.payment_entries || [];
  const hasPaymentLegs = paymentLegs.length > 0;
  const paymentPillSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1.5,
    py: 0.5,
    bgcolor: 'success.50',
    borderRadius: '8px',
    border: '1px solid #378666ff',
    borderColor: 'success.200',
  };

  // How far the settlement has got. A draft is no longer proof that nothing was paid:
  // it can be mid-split, holding a captured card leg and still owing the rest.
  const legsTotal = paymentLegs.reduce((sum, leg) => sum + (parseFloat(leg.amount) || 0), 0);
  const invoiceTotalNum = parseFloat(invoice?.total_incl_vat) || 0;
  const isPartiallySettled = Boolean(invoice?.is_partially_settled);
  const remainingToPay = invoiceTotalNum - legsTotal;

  // An ISSUED invoice holding part of its money (osasuoritus), and its payment ledger.
  // The table shows every row once the invoice is open with money on it, or once more
  // than one thing happened to it; a plain single-payment invoice keeps its one-line story.
  const isPartiallyPaid = Boolean(invoice?.is_partially_paid);
  const ledgerRows = invoice?.payments || [];
  const showPaymentsTable = ledgerRows.length > 0 && (invoice?.status !== 'paid' || ledgerRows.length > 1);
  const canReverseRow = (row) =>
    ['manual', 'bank_import', 'migration'].includes(row.source)
    && parseFloat(row.amount) >= 0
    && !row.refunded_at
    && !row.reverses;

  const terminalTxns = invoice?.terminal_transactions || [];
  const isRenderableCardTxn = (txn) =>
    Boolean(txn)
    && txn.transaction_type === 'purchase'
    && ['awaiting_confirm', 'confirmed'].includes(txn.status)
    && Boolean(txn.card_scheme);

  // One entry per CARD leg, each joined to the capture that took it BY ID.
  //
  // Never by position: terminal_transactions comes back newest-first (the model orders by
  // -created_at) while payment_entries is id-ascending, so pairing them off would show one
  // card's masked PAN next to another card's amount. `txn` is null for a manual card
  // tender, where the workshop charged the card on its own device and there is no capture.
  const txnById = new Map(terminalTxns.map((txn) => [txn.id, txn]));
  const cardLegRows = paymentLegs
    .filter((leg) => leg.payment_method === 'card')
    .map((leg) => {
      const txn = txnById.get(leg.terminal_transaction);
      return { key: `leg-${leg.id}`, amount: leg.amount, txn: isRenderableCardTxn(txn) ? txn : null };
    });

  // An ordinary single-method card invoice has no legs at all; payment_method is the
  // whole story and the newest successful purchase is the one that paid it.
  const legacyCardTxn =
    !hasPaymentLegs && invoice?.payment_method === 'card'
      ? terminalTxns.filter(isRenderableCardTxn)[0] || null
      : null;

  const cardDetailRows = cardLegRows.length
    ? cardLegRows
    : legacyCardTxn
      ? [{ key: `txn-${legacyCardTxn.id}`, amount: null, txn: legacyCardTxn }]
      : [];

  // The credit went through but Amili could not be told to stop collecting: the original is
  // flagged (amili_conflict_at) and the user has to cancel the collection in Amili by hand.
  const warnIfAmiliCancelFailed = (amiliCancel) => {
    if (!amiliCancel?.attempted || amiliCancel.success) return false;
    setSnackbar({ open: true, message: t('amili.creditCancelFailed'), severity: 'error' });
    return true;
  };

  const handleCreateCreditNote = async () => {
    try {
      const response = await axios.post(`/api/invoices/${id}/create_credit_note/`);
      setSnackbar({
        open: true,
        message: t('invoices.creditNoteCreated'),
        severity: 'success',
      });
      // Navigate to the new credit note
      navigate(`/invoices/${response.data.id}`);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error_code
          ? t(errorKey(err.response.data.error_code), err.response.data.error)
          : (err.response?.data?.error || t('Failed to create credit note')),
        severity: 'error',
      });
    }
  };

  // "Korjaa lasku": credit this invoice and open the corrected draft in one step, instead of
  // credit note -> re-type every line by hand (which loses the work order link).
  const handleCorrectInvoice = async () => {
    setCorrecting(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/correct/`);
      if (!warnIfAmiliCancelFailed(response.data.amili_cancel)) {
        setSnackbar({
          open: true,
          message: t('invoices.correctionCreated'),
          severity: 'success',
        });
      }
      navigate(`/invoices/${response.data.correction.id}`);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error_code
          ? t(errorKey(err.response.data.error_code), err.response.data.error)
          : t('invoices.correctionFailed'),
        severity: 'error',
      });
    } finally {
      setCorrecting(false);
      setCorrectDialogOpen(false);
    }
  };

  // "Hyvitä" for a credit note that was already delivered (issued before settlement
  // existed): settle it, nothing to send. Draft credit notes are settled inside
  // handleSendUnified instead, so crediting and sending stay a single action.
  // `chosenRefundMethod` is deliberately not named `refundMethod`: that would shadow the state
  // of the same name and a later edit could silently read the wrong one.
  const handleApplyCredit = async (chosenRefundMethod = null, restock = false) => {
    // A double click must not send the credit twice. The backend locks the row as well;
    // this just spares the user an "already applied" error on the second request.
    if (applyingCredit) return;
    setApplyingCredit(true);
    try {
      const response = await axios.post(
        `/api/invoices/${id}/apply_credit/`,
        {
          ...(chosenRefundMethod ? { refund_method: chosenRefundMethod } : {}),
          ...(restock ? { restock_parts: true } : {}),
        },
      );
      const amiliFailed = warnIfAmiliCancelFailed(response.data.amili_cancel);
      const restockFailed = notifyRestock(response.data.restock);
      if (!amiliFailed && !restockFailed) {
        const returned = response.data.restock?.returned || 0;
        const base = response.data.credit_outcome === 'refund_pending'
          ? t('invoices.creditAppliedRefundPending')
          : t('invoices.creditApplied');
        setSnackbar({
          open: true,
          message: returned > 0 ? `${base} ${t('invoices.restockDone', { count: returned })}` : base,
          severity: 'success',
        });
      }
      fetchInvoice();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error_code
          ? t(errorKey(err.response.data.error_code), err.response.data.error)
          : t('invoices.creditApplyFailed'),
        severity: 'error',
      });
    } finally {
      setApplyingCredit(false);
    }
  };

  const handleMarkRefunded = async () => {
    try {
      await axios.post(`/api/invoices/${id}/mark_refunded/`);
      setSnackbar({
        open: true,
        message: t('invoices.refundRecorded'),
        severity: 'success',
      });
      fetchInvoice();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error_code
          ? t(errorKey(err.response.data.error_code), err.response.data.error)
          : t('invoices.refundRecordFailed'),
        severity: 'error',
      });
    } finally {
      setMarkRefundedDialogOpen(false);
    }
  };

  // "Peru maksumerkintä" — undo a payment mark made by hand. Admin only; the backend refuses
  // when Amili/Svea/terminal/split legs hold the money.
  const handleUnmarkPaid = async () => {
    setUnmarkingPaid(true);
    try {
      await axios.post(`/api/invoices/${id}/unmark_paid/`, { reason: unmarkPaidReason });
      setSnackbar({
        open: true,
        message: t('invoices.paymentMarkReverted'),
        severity: 'success',
      });
      fetchInvoice();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error_code
          ? t(errorKey(err.response.data.error_code), err.response.data.error)
          : t('invoices.paymentMarkRevertFailed'),
        severity: 'error',
      });
    } finally {
      setUnmarkingPaid(false);
      setUnmarkPaidDialogOpen(false);
      setUnmarkPaidReason('');
    }
  };

  const handleMarkCashPayment = async (paymentMethod) => {
    setMarkingCashPayment(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/mark_cash_payment/`, {
        payment_method: paymentMethod,
        mark_as_paid: true,
      });
      setInvoice(response.data);
      const methodLabel = paymentMethod === 'cash' ? t('Cash payment') : t('Card payment');
      setSnackbar({
        open: true,
        message: `${methodLabel} - ${t('marked as paid')}`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('Failed to mark payment'),
        severity: 'error',
      });
    } finally {
      setMarkingCashPayment(false);
    }
  };

  const handleMarkFinancingPayment = async () => {
    setMarkingFinancing(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/mark_financing_payment/`, {
        financing_reference: financingReference,
      });
      setInvoice(response.data);
      setFinancingDialogOpen(false);
      setFinancingReference('');
      setSnackbar({
        open: true,
        message: `${t('financingPayment')} - ${t('marked as paid')}`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('Failed to mark payment'),
        severity: 'error',
      });
    } finally {
      setMarkingFinancing(false);
    }
  };

  const handleMarkInvoicedElsewhere = async () => {
    setMarkingInvoicedElsewhere(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/mark_invoiced_elsewhere/`, {
        external_billing_reference: invoicedElsewhereReference,
        paid_date: invoicedElsewhereDate || undefined,
      });
      setInvoice(response.data);
      setInvoicedElsewhereDialogOpen(false);
      setInvoicedElsewhereReference('');
      setSnackbar({
        open: true,
        message: t('invoices.invoicedElsewhereDone'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('Failed to mark payment'),
        severity: 'error',
      });
    } finally {
      setMarkingInvoicedElsewhere(false);
    }
  };

  // Terminal card payment handler
  const handleTerminalPayment = async (terminalPk) => {
    setTerminalPaymentLoading(true);
    setTerminalSelectOpen(false);
    try {
      const response = await axios.post(`/api/invoices/${id}/terminal-payment/`, {
        terminal_id: terminalPk,
      }, { timeout: 60000 });
      setInvoice(response.data);
      setSnackbar({
        open: true,
        message: t('terminal.paymentSuccessful'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: terminalErrorMessage(t, err.response?.data, 'terminal.paymentFailed'),
        severity: 'error',
      });
    } finally {
      setTerminalPaymentLoading(false);
    }
  };

  // Handle card payment button click - use terminal if available, manual otherwise
  const handleCardPayment = () => {
    // Terminal payment only works on a draft invoice; guard against a stale or
    // duplicate submit so we don't fire a request the backend will reject with
    // "Only draft invoices can be paid via terminal" (VERSTAANI-BACKEND-CH).
    if (!isDraftInvoice || terminalPaymentLoading || markingCashPayment) return;
    // A half-paid draft already holds a captured leg, so "card payment" here would ask
    // the reader for the WHOLE total on top of it. The split dialog is the continue path:
    // it knows what is left and charges only that.
    if (isPartiallySettled) {
      setSplitPaymentDialogOpen(true);
      return;
    }
    if (paymentTerminalEnabled && terminals.length > 0) {
      if (terminals.length === 1) {
        handleTerminalPayment(terminals[0].id);
      } else {
        setTerminalSelectOpen(true);
      }
    } else {
      handleMarkCashPayment('card');
    }
  };


  // Terminal refund handler
  /**
   * Reverse a sale taken with several tenders, one leg per request.
   *
   * Each card goes back on the reader for its own amount; a cash or manual card leg is
   * handed over the counter and the request only records that. The server marks the
   * invoice refunded when the last leg is back, so a run that stops half way can simply be
   * repeated: legs already given back are skipped and the rest are offered again.
   */
  const refundTenderLegs = async (terminalPk) => {
    const legs = (invoice?.payment_entries || []).filter((leg) => !leg.refunded_at);
    for (let i = 0; i < legs.length; i += 1) {
      setRefundProgress({ index: i + 1, total: legs.length });
      for (let attempt = 1; ; attempt += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await axios.post(
            `/api/invoices/${id}/payment-leg/${legs[i].id}/refund/`,
            { terminal_id: terminalPk, customer_not_present: refundCustomerNotPresent },
            { timeout: 60000 },
          );
          break;
        } catch (err) {
          // The reader is often still finishing the previous refund; that clears by itself.
          if (attempt < TERMINAL_RETRY_ATTEMPTS && isTransientTerminalCondition(err.response?.data)) {
            setSnackbar({
              open: true,
              message: t('terminal.busyRetry', { attempt: attempt + 1, total: TERMINAL_RETRY_ATTEMPTS }),
              severity: 'warning',
            });
            // eslint-disable-next-line no-await-in-loop
            await waitBeforeTerminalRetry();
            continue;
          }
          throw err;
        }
      }
    }
  };

  const handleTerminalRefund = async (terminalPk) => {
    setRefundLoading(true);
    setRefundDialogOpen(false);
    setRefundTerminalSelectOpen(false);
    try {
      if (hasTenderLegs) {
        await refundTenderLegs(terminalPk);
        await fetchInvoice();
      } else {
        const response = await axios.post(`/api/invoices/${id}/terminal-refund/`, {
          terminal_id: terminalPk,
          customer_not_present: refundCustomerNotPresent,
        }, { timeout: 60000 });
        setInvoice(response.data);
      }
      setSnackbar({
        open: true,
        // The usual reason for a refund at the counter is a mistake in the sale, so point
        // at the way back: copy the invoice, fix it, charge again.
        message: t('terminal.refundedCopyHint'),
        severity: 'success',
      });
      setRefundCustomerNotPresent(false);
    } catch (err) {
      // Some legs may already be back; refetch so the dialog shows what is left to do.
      if (hasTenderLegs) await fetchInvoice();
      setSnackbar({
        open: true,
        message: terminalErrorMessage(t, err.response?.data, 'terminal.refundFailed'),
        severity: 'error',
      });
    } finally {
      setRefundProgress(null);
      setRefundLoading(false);
    }
  };

  // Start refund flow
  const handleRefundClick = () => {
    setRefundCustomerNotPresent(false);
    setRefundDialogOpen(true);
  };

  // Confirm refund - select terminal if multiple
  const handleRefundConfirm = () => {
    if (terminals.length === 1) {
      handleTerminalRefund(terminals[0].id);
    } else {
      setRefundDialogOpen(false);
      setRefundTerminalSelectOpen(true);
    }
  };

  // Copy as new draft handler
  const handleCopyAsDraft = async () => {
    setCopyingAsDraft(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/copy-as-draft/`);
      setSnackbar({
        open: true,
        message: t('invoices.copyAsDraftSuccess'),
        severity: 'success',
      });
      navigate(`/invoices/${response.data.id}`);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('Error'),
        severity: 'error',
      });
    } finally {
      setCopyingAsDraft(false);
    }
  };

  // Svea financing handler
  const handleCreateSveaFinancing = async () => {
    setSveaLoading(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/svea-financing/`);
      setInvoice(response.data);
      setSveaDialogOpen(true);
      setSnackbar({ open: true, message: t('svea.financingCreated', 'Rahoitushakemus luotu'), severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('svea.financingFailed', 'Rahoitushakemuksen luonti epäonnistui'),
        severity: 'error',
      });
    } finally {
      setSveaLoading(false);
    }
  };

  // Svea S2S handlers
  const handleOpenS2sDialog = () => {
    setSveaS2sResult(null);
    setSveaS2sHetu('');
    setSveaS2sPaymentMethod('FI70');
    setSveaS2sPlans([]);
    setSveaS2sSelectedPlan(null);
    setSveaS2sDialogOpen(true);
  };

  const handleFetchPlans = async () => {
    setSveaS2sPlansLoading(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/svea-payment-plans/`);
      setSveaS2sPlans(response.data.plans || []);
    } catch (err) {
      setSnackbar({ open: true, message: t('svea.decisionError'), severity: 'error' });
    } finally {
      setSveaS2sPlansLoading(false);
    }
  };

  const handleS2sSubmit = async () => {
    setSveaS2sLoading(true);
    setSveaS2sResult(null);
    try {
      const response = await axios.post(`/api/invoices/${id}/svea-s2s-financing/`, {
        hetu: sveaS2sHetu,
        payment_method: sveaS2sPaymentMethod,
        campaign_code: sveaS2sSelectedPlan?.CampaignCode || '',
      });
      setInvoice(response.data);
      setSveaS2sResult({ success: true });
      setSnackbar({ open: true, message: t('svea.decisionApproved', 'Rahoituspäätös hyväksytty!'), severity: 'success' });
      setTimeout(() => setSveaS2sDialogOpen(false), 1500);
    } catch (err) {
      const data = err.response?.data || {};
      if (data.denied) {
        setSveaS2sResult({ denied: true, message: data.svea_error_text || data.error });
      } else {
        setSveaS2sResult({ error: true, message: data.error || t('svea.decisionError') });
      }
    } finally {
      setSveaS2sLoading(false);
    }
  };

  // Withdraw the open financing request (Svea link or Resurs SMS link)
  const handleCancelFinancing = async () => {
    setFinancingCancelLoading(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/financing-cancel/`);
      setInvoice(response.data);
      setSnackbar({ open: true, message: t('financing.requestCancelled'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || t('financing.cancelFailed'), severity: 'error' });
    } finally {
      setFinancingCancelLoading(false);
    }
  };

  // Resurs refund handler
  const handleResursRefund = async () => {
    setResursRefundLoading(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/resurs-refund/`);
      setInvoice(response.data);
      setResursRefundDialogOpen(false);
      setSnackbar({ open: true, message: t('resurs.refundSuccessful'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || t('resurs.refundFailed'), severity: 'error' });
    } finally {
      setResursRefundLoading(false);
    }
  };

  // Svea refund handler
  const handleSveaRefund = async () => {
    setSveaRefundLoading(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/svea-refund/`);
      setInvoice(response.data);
      setSveaRefundDialogOpen(false);
      if (response.data.bank_transfer_required) {
        setSveaBankTransfer(response.data.bank_transfer);
      } else {
        setSnackbar({ open: true, message: t('svea.refundSuccessful'), severity: 'success' });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('svea.refundFailed'),
        severity: 'error',
      });
    } finally {
      setSveaRefundLoading(false);
    }
  };

  // Reverse a payment recorded by hand or from a bank statement (admin only). A verb that
  // writes an offsetting row; the ledger reopens the invoice when the rows no longer cover it.
  const handleReversePayment = async () => {
    if (!reversePaymentTarget) return;
    setReversingPayment(true);
    try {
      await axios.post(`/api/invoices/${id}/payments/${reversePaymentTarget.id}/reverse/`, {});
      setSnackbar({ open: true, message: t('invoices.paymentReversed'), severity: 'success' });
      setReversePaymentTarget(null);
      fetchInvoice();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error_code
          ? t(errorKey(err.response.data.error_code), err.response.data.error)
          : (err.response?.data?.error || t('invoices.paymentReverseFailed')),
        severity: 'error',
      });
    } finally {
      setReversingPayment(false);
    }
  };

  // The bank statement import hands an Amili-monitored bank line over here: open the
  // ohisuoritus dialog with the line's amount and booking date already filled in, once.
  useEffect(() => {
    const prefillAmount = searchParams.get('ohisuoritus');
    if (prefillAmount == null || !invoice) return;
    setOhisuoritusAmount(prefillAmount);
    setOhisuoritusDate(searchParams.get('date') || dateToDateString(new Date()));
    setOhisuoritusError('');
    setOhisuoritusDialogOpen(true);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id]);

  // The quick payment buttons on the work order create the invoice and land here with
  // ?pay=cash|card: run that payment once, exactly as if its button had been clicked. The
  // param is removed first so a reload or Back can never take the payment a second time.
  const quickPayHandledFor = useRef(null);
  useEffect(() => {
    const pay = searchParams.get('pay');
    if (pay !== 'cash' && pay !== 'card') return;
    if (loading || !invoice || String(invoice.id) !== String(id)) return;
    if (pay === 'card' && !terminalsLoaded) return;
    if (quickPayHandledFor.current === invoice.id) return;
    quickPayHandledFor.current = invoice.id;

    const next = new URLSearchParams(searchParams);
    next.delete('pay');
    setSearchParams(next, { replace: true });

    if (invoice.status !== 'draft' || invoice.invoice_type === 'INV02') return;
    if (pay === 'cash') {
      handleMarkCashPayment('cash');
    } else {
      handleCardPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id, loading, terminalsLoaded, searchParams]);

  // Ohisuoritus: the field opens with the open balance filled in, and what is typed is
  // measured against it before anything is sent. Invoice 20260318 was marked paid on an
  // "18.08" typed into an empty amount field; the backend refuses that now, and this dialog
  // says so before the click.
  const ohisuoritusOpenBalance = parseFloat(invoice?.open_balance ?? invoice?.total_incl_vat ?? 0) || 0;
  const ohisuoritusValue = parseFloat(ohisuoritusAmount);
  const ohisuoritusCovers = !Number.isNaN(ohisuoritusValue)
    && ohisuoritusValue >= ohisuoritusOpenBalance - PAYMENT_ROUNDING_TOLERANCE;
  const ohisuoritusMonitored = isAmiliMonitored(invoice);
  const ohisuoritusPartialRefused = ohisuoritusMonitored && !Number.isNaN(ohisuoritusValue) && !ohisuoritusCovers;
  const ohisuoritusOverBalance = !Number.isNaN(ohisuoritusValue)
    && ohisuoritusValue > ohisuoritusOpenBalance + PAYMENT_ROUNDING_TOLERANCE;
  const ohisuoritusConflictClaim = amiliOpenClaim(invoice);
  const showOhisuoritusButton = Boolean(invoice?.amili_assignment_id)
    && invoice?.status !== 'paid' && invoice?.amili_payment_status !== 'paid';
  const showOhisuoritusConflictButton = isAmiliConflictPayable(invoice) && ohisuoritusConflictClaim > 0;
  const ohisuoritusValid = ohisuoritusConflict
    ? ohisuoritusConflictClaim > 0 && Boolean(ohisuoritusDate)
    : !Number.isNaN(ohisuoritusValue) && ohisuoritusValue > 0
      && !ohisuoritusPartialRefused && !ohisuoritusOverBalance && Boolean(ohisuoritusDate);

  const openOhisuoritusDialog = (conflict = false) => {
    setOhisuoritusConflict(conflict);
    setOhisuoritusAmount(String(invoice?.open_balance ?? invoice?.total_incl_vat ?? ''));
    setOhisuoritusDate(dateToDateString(new Date()));
    setOhisuoritusError('');
    setOhisuoritusDialogOpen(true);
  };

  const handleOhisuoritus = async () => {
    if (!ohisuoritusValid) return;
    setOhisuoritusError('');
    setSubmittingOhisuoritus(true);
    try {
      await axios.post(`/api/invoices/${id}/mark_paid_externally/`, {
        amount: ohisuoritusConflict ? ohisuoritusConflictClaim.toFixed(2) : ohisuoritusAmount,
        paid_date: ohisuoritusDate,
      });
      setSnackbar({
        open: true,
        message: t('amili.externalPaymentReported', 'Ohisuoritus ilmoitettu'),
        severity: 'success',
      });
      setOhisuoritusDialogOpen(false);
      fetchInvoice();
    } catch (err) {
      const code = err.response?.data?.error_code;
      setOhisuoritusError(
        code
          ? t(errorKey(code), err.response.data.error)
          : (err.response?.data?.error || t('amili.externalPaymentFailed', 'Ohisuorituksen ilmoitus epäonnistui')),
      );
    } finally {
      setSubmittingOhisuoritus(false);
    }
  };

  // One line per Amili event, in words: "Maksumuistutus lähetetty 1.9.2026", "Suoritus
  // kirjattu 18,08 €". Unknown types fall back to the raw code so nothing is hidden.
  const describeAmiliEvent = (event) => {
    const type = String(event?.type || '').toLowerCase();
    const data = event?.data || {};
    const key = AMILI_EVENT_KEYS[type];
    if (!key) return event?.type || event?.description || JSON.stringify(event);
    const sum = data.sum_paid ?? data.sum ?? data.credit_sum;
    return t(key, {
      amount: sum != null ? formatCurrency(sum) : '',
      code: data.code || '',
      reason: data.reason || data.comment || '',
      date: formatDate(data.end_date || data.due_date || data.sent_at || data.started_at || data.booked_at || data.happened_at || null),
    });
  };

  const handleMarkAsSent = async () => {
    setMarkingAsSent(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/mark_as_sent/`);
      setInvoice(response.data);
      setMarkAsSentDialogOpen(false);
      setSnackbar({
        open: true,
        message: t('Invoice marked as sent'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('Failed to mark invoice as sent'),
        severity: 'error',
      });
    } finally {
      setMarkingAsSent(false);
    }
  };

  const handleApplyDiscount = async () => {
    const percent = parseFloat(discountPercent);
    if (isNaN(percent) || percent <= 0 || percent > 100) {
      setSnackbar({
        open: true,
        message: t('Enter a valid discount percentage (0.01 - 100)'),
        severity: 'error',
      });
      return;
    }

    setApplyingDiscount(true);
    try {
      const response = await axios.post(`/api/invoices/${id}/apply_discount/`, {
        discount_percent: discountPercent,
      });
      setInvoice(response.data);
      setApplyDiscountDialogOpen(false);
      setDiscountPercent('');
      setSnackbar({
        open: true,
        message: t('Discount applied successfully'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('Failed to apply discount'),
        severity: 'error',
      });
    } finally {
      setApplyingDiscount(false);
    }
  };

  const getDominantVatRate = () => {
    if (!invoice?.lines?.length) return 25.5;
    // Group lines by vat_rate: count occurrences and sum absolute totals
    const groups = {};
    for (const line of invoice.lines) {
      const rate = parseFloat(line.vat_rate);
      if (!groups[rate]) groups[rate] = { count: 0, absTotal: 0 };
      groups[rate].count += 1;
      groups[rate].absTotal += Math.abs(parseFloat(line.line_total_incl_vat || 0));
    }
    // Find rate with most lines; on tie, use highest absolute total
    let bestRate = parseFloat(invoice.lines[0].vat_rate);
    let bestCount = 0;
    let bestAbsTotal = 0;
    for (const [rate, info] of Object.entries(groups)) {
      if (info.count > bestCount || (info.count === bestCount && info.absTotal > bestAbsTotal)) {
        bestRate = parseFloat(rate);
        bestCount = info.count;
        bestAbsTotal = info.absTotal;
      }
    }
    return bestRate;
  };

  const handleRoundTotal = async () => {
    const desired = parseFloat(desiredTotal);
    const currentTotal = parseFloat(invoice.total_incl_vat);

    if (isNaN(desired) || desired <= 0) {
      setSnackbar({
        open: true,
        message: t('Enter a valid price'),
        severity: 'error',
      });
      return;
    }

    if (desired >= currentTotal) {
      setSnackbar({
        open: true,
        message: t('Desired price must be less than current total'),
        severity: 'error',
      });
      return;
    }

    const differenceInclVat = Math.round((currentTotal - desired) * 100) / 100;
    const vatRate = getDominantVatRate();

    setRoundingTotal(true);
    try {
      await axios.post(`/api/invoices/${id}/lines/`, {
        line_type: 'custom',
        description: t('Rounding'),
        quantity: -1,
        unit: 'kpl',
        unit_price_incl_vat: differenceInclVat,
        price_entered_with_vat: true,
        vat_rate: vatRate,
      });
      const response = await axios.get(`/api/invoices/${id}/`);
      setInvoice(response.data);
      setRoundTotalDialogOpen(false);
      setDesiredTotal('');
      setSnackbar({
        open: true,
        message: t('Rounding applied successfully'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('Failed to apply rounding'),
        severity: 'error',
      });
    } finally {
      setRoundingTotal(false);
    }
  };

  // Invoice editing handlers
  const handleLineClick = (line) => {
    if (invoice.status === 'draft') {
      setSelectedLine(line);
      setEditLineDialogOpen(true);
    }
  };

  /**
   * Move a line one step up/down and persist the whole order.
   *
   * Sends the complete id list rather than a pairwise swap: the endpoint rewrites
   * display_order in one atomic pass, so a dropped request can never leave the
   * invoice half-reordered. Applies optimistically and rolls back on failure.
   */
  const handleMoveLine = async (index, direction) => {
    if (reordering) return;
    const lines = invoice?.lines || [];
    const target = index + direction;
    if (target < 0 || target >= lines.length) return;

    const reordered = [...lines];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    const previous = invoice;
    setInvoice((prev) => ({ ...prev, lines: reordered }));
    setReordering(true);
    try {
      const response = await axios.post(
        `/api/invoices/${invoice.id}/reorder-lines/`,
        { lines: reordered.map((line) => line.id) },
      );
      setInvoice(response.data);
    } catch (err) {
      setInvoice(previous);
      setSnackbar({
        open: true,
        message: t('Reordering failed. Please try again.'),
        severity: 'error',
      });
    } finally {
      setReordering(false);
    }
  };

  const handleInvoiceUpdate = (updatedInvoice) => {
    setInvoice(updatedInvoice);
  };

  const isDraft = invoice?.status === 'draft';

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  if (error && !invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/invoices')} sx={{ mt: 2 }}>
          {t('Back to invoices')}
        </Button>
      </Box>
    );
  }

  if (!invoice) return null;

  return (
    <Box data-testid="invoice-detail-page">
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/invoices')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }} data-testid="invoice-number">
            {invoice.invoice_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {invoice.customer_name}
          </Typography>
        </Box>
        <Box data-testid="invoice-status">
          <InvoiceStatusBadge status={invoice.is_overdue ? 'overdue' : invoice.status} invoice_type={invoice.invoice_type} settlement_status={invoice.settlement_status} partially_paid={isPartiallySettled || isPartiallyPaid} size="medium" />
        </Box>
        {hasPaymentLegs ? (
          // Split payment: one pill per tender leg with its amount, so the header never
          // claims the whole sum went on the card.
          <Box data-testid="invoice-payment-legs" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {paymentLegs.map((leg) => (
              <Box key={leg.id} sx={paymentPillSx}>
                {leg.payment_method === 'card' ? (
                  <CreditCardIcon sx={{ fontSize: 16, color: 'success.main' }} />
                ) : (
                  <PaymentsIcon sx={{ fontSize: 16, color: 'success.main' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                  {paymentMethodLabels[leg.payment_method] || leg.payment_method} {formatCurrency(leg.amount)}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (isCashOrCard || ['financing', 'refund_bank', 'invoiced_elsewhere'].includes(invoice.payment_method)) && (
          <Box sx={paymentPillSx} data-testid="invoice-payment-method-pill">
            {invoice.payment_method === 'invoiced_elsewhere' ? (
              <OutboxIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : ['financing', 'refund_bank'].includes(invoice.payment_method) ? (
              <AccountBalanceIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <PaymentsIcon sx={{ fontSize: 16, color: 'success.main' }} />
            )}
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
              {paymentMethodLabels[invoice.payment_method] || invoice.payment_method}
            </Typography>
          </Box>
        )}
        {isPartiallySettled && (
          <Alert
            severity="warning"
            data-testid="invoice-partial-payment-alert"
            sx={{ width: '100%', mt: 1 }}
            action={
              <Button color="inherit" size="small" onClick={() => setSplitPaymentDialogOpen(true)}>
                {t('invoices.continuePayment')}
              </Button>
            }
          >
            {t('invoices.partiallyPaidBanner', {
              paid: formatCurrency(legsTotal),
              remaining: formatCurrency(remainingToPay),
            })}
          </Alert>
        )}
        {isPartiallyPaid && (
          <Alert severity="warning" data-testid="invoice-partially-paid-alert" sx={{ width: '100%', mt: 1 }}>
            {t('invoices.partiallyPaidIssuedBanner', {
              paid: formatCurrency(invoice.amount_paid),
              remaining: formatCurrency(invoice.open_balance),
            })}
          </Alert>
        )}
        {['payment_cash', 'payment_card', 'payment_invoice', 'billing_customer', 'customer_share'].includes(invoice.invoice_split_type) && (
          <Chip
            size="small"
            icon={<CallSplitIcon />}
            label={t(`splitType.${invoice.invoice_split_type}`)}
            color={invoice.invoice_split_type.startsWith('payment_') ? 'secondary' : 'info'}
            variant="outlined"
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        {/* Actions dropdown menu */}
        <Button
          variant="outlined"
          endIcon={<ExpandMoreIcon />}
          onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
        >
          {t('Actions')}
        </Button>
        <Menu
          anchorEl={actionsMenuAnchor}
          open={Boolean(actionsMenuAnchor)}
          onClose={() => setActionsMenuAnchor(null)}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: '10px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            },
          }}
        >
          {/* Hide PDF buttons for unsent Amili invoices (local PDF has wrong IBAN) */}
          {!(amiliEnabled && !invoice.maventa_invoice_id && invoice.status === 'draft') && (
            <>
              <MenuItem
                onClick={() => {
                  handlePreviewPDF();
                  setActionsMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {isCashOrCard ? t('Preview receipt') : t('Preview PDF')}
                </ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  handleDownloadPDF();
                  setActionsMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {isCashOrCard ? t('Download receipt') : t('Download PDF')}
                </ListItemText>
              </MenuItem>
            </>
          )}

          {showPdfActions && showEditActions && <Divider />}

          {invoice.status === 'draft' && (
            <MenuItem
              onClick={() => {
                setEditHeaderDialogOpen(true);
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('Edit details')}</ListItemText>
            </MenuItem>
          )}

          {(invoice.status === 'draft' || (invoice.status === 'sent' && invoice.maventa_status === 'failed')) && (
            <MenuItem
              onClick={() => {
                setInsuranceDialogOpen(true);
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <HealthAndSafetyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('Insurance details')}</ListItemText>
            </MenuItem>
          )}

          {/* Attachments are draft-only, like every other invoice edit. This is the
              entry point for the first one: the attachments card only appears on
              the invoice once at least one file exists. */}
          {invoice.status === 'draft' && (
            <MenuItem
              onClick={() => {
                setActionsMenuAnchor(null);
                attachmentsRef.current?.openFilePicker();
              }}
            >
              <ListItemIcon>
                <AttachFileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('invoiceAttachments.add', 'Lisää liite')}</ListItemText>
            </MenuItem>
          )}

          {invoice.status === 'draft' && invoice.maintenance && (
            <MenuItem
              onClick={() => {
                setActionsMenuAnchor(null);
                attachmentsRef.current?.openWorkOrderDialog();
              }}
            >
              <ListItemIcon>
                <BuildIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {t('invoiceAttachments.addFromWorkOrder', 'Lisää liite työmääräyksestä')}
              </ListItemText>
            </MenuItem>
          )}

          {invoice.status === 'draft' && (
            <MenuItem
              onClick={() => {
                setApplyDiscountDialogOpen(true);
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <LocalOfferIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('Apply discount')}</ListItemText>
            </MenuItem>
          )}

          {invoice.status === 'draft' && (
            <MenuItem
              onClick={() => {
                setRoundTotalDialogOpen(true);
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <LocalOfferIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('Round final price')}</ListItemText>
            </MenuItem>
          )}

          {(showPdfActions || showEditActions) && showPaymentActions && <Divider />}

          {invoice.status === 'draft' && invoice.invoice_split_type === 'normal' && (
            <MenuItem
              onClick={() => {
                setSplitPaymentDialogOpen(true);
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <CallSplitIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('Split payment')}</ListItemText>
            </MenuItem>
          )}

          {/* Manual card payment fallback (when terminals are active, main button uses terminal) */}
          {invoice.status === 'draft' && paymentTerminalEnabled && terminals.length > 0 && (
            <MenuItem
              onClick={() => {
                handleMarkCashPayment('card');
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <CreditCardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('terminal.manualCardPayment')}</ListItemText>
            </MenuItem>
          )}

          {/* Manual financing (Rahoitus) - external financiers like Resursbank, no integration */}
          {showManualFinancing && (
            <MenuItem
              onClick={() => {
                setFinancingReference(invoice.financing_reference || '');
                setFinancingDialogOpen(true);
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <AccountBalanceIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('Mark financing manually')}</ListItemText>
            </MenuItem>
          )}

          {/* Laskutettu muualla - billed and collected by another system (a chain's own
              billing); recorded here as paid so it shows in the reports, never sent from here
              and kept out of the accounting transfers. Same gate as manual financing. */}
          {showManualFinancing && (
            <MenuItem
              data-testid="menu-invoiced-elsewhere"
              onClick={() => {
                setInvoicedElsewhereReference('');
                setInvoicedElsewhereDate(dateToDateString(new Date()));
                setInvoicedElsewhereDialogOpen(true);
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <OutboxIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('invoices.markInvoicedElsewhere')}</ListItemText>
            </MenuItem>
          )}

          {invoice.status === 'draft' && (
            <MenuItem
              onClick={() => {
                setMarkAsSentDialogOpen(true);
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('Mark as sent')}</ListItemText>
            </MenuItem>
          )}

          {/* Korjaa lasku - credit + corrected draft in one step. The common case when an
              invoice went out wrong; keeps the work order link and the original lines.
              Hidden for the cash/card legs of a split: they hold an "Ennakkomaksu" line
              rather than the work rows, so the corrected copy would be meaningless. */}
          {!['draft', 'cancelled', 'combined', 'refunded'].includes(invoice.status) &&
           invoice.invoice_type !== 'INV02' &&
           !isInvoicedElsewhere &&
           !isImmediateSplitChild &&
           !invoice.settlement_status && (
            <MenuItem
              onClick={() => {
                setCorrectDialogOpen(true);
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('invoices.correctInvoice')}</ListItemText>
            </MenuItem>
          )}

          {/* Create Credit Note - for sent/paid/overdue invoices, cash/card included: a
              partial card refund taken on the workshop's own terminal is recorded exactly
              this way. Terminal-refunded invoices are excluded (the sale is already
              reversed; the backend refuses those too). */}
          {!['draft', 'cancelled', 'combined', 'refunded'].includes(invoice.status) &&
           invoice.invoice_type !== 'INV02' &&
           !isInvoicedElsewhere &&
           !invoice.settlement_status && (
            <MenuItem
              onClick={() => {
                handleCreateCreditNote();
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <UndoIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('invoices.createCreditNote')}</ListItemText>
            </MenuItem>
          )}

          {/* Peru maksumerkintä - undo a hand-made payment mark (admin only; the backend
              refuses when Amili/Svea/terminal/split entries hold the money) */}
          {invoice.status === 'paid' && !invoice.amili_assignment_id && !isInvoicedElsewhere && (
            <MenuItem
              onClick={() => {
                setUnmarkPaidDialogOpen(true);
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <UndoIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('invoices.unmarkPaid')}</ListItemText>
            </MenuItem>
          )}

          {/* Edit the linked customer and reflect the changes onto this invoice
              (draft or failed e-invoice only) */}
          {showEditActions && invoice?.customer_id && (
            <MenuItem
              onClick={() => {
                openEditCustomerDialog();
                setActionsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('invoices.editCustomerInfo', 'Muokkaa asiakkaan tietoja')}</ListItemText>
            </MenuItem>
          )}

          {(showPdfActions || showEditActions || showPaymentActions) && <Divider />}

          <MenuItem
            onClick={() => {
              setEventLogDialogOpen(true);
              setActionsMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('Event log')}</ListItemText>
          </MenuItem>

          {/* Rollback to draft - only for sent non-verkkolasku invoices */}
          {canRollbackToDraft && (
            <>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleRollbackToDraft();
                  setActionsMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <UndoIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('Rollback to draft')}</ListItemText>
              </MenuItem>
            </>
          )}

          {/* Cancel - for drafts and cash/card/email invoices */}
          {canCancel && (
            <>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleCancel();
                  setActionsMenuAnchor(null);
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon>
                  <CancelIcon fontSize="small" sx={{ color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText>{t('Cancel invoice')}</ListItemText>
              </MenuItem>
            </>
          )}
        </Menu>

        {/* A draft credit note has exactly ONE action: "Hyvitä", which applies the credit and
            then opens the send dialog so it reaches the customer. Send/Card/Cash are hidden —
            "Lähetä" alongside "Hyvitä" was two buttons each doing half the job, and taking a
            cash or card payment on a negative document is meaningless. */}
        {invoice.status === 'draft' && invoice.invoice_type !== 'INV02' && (
          <>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={openSendDialog}
            >
              {t('Send invoice')}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={(markingCashPayment || terminalPaymentLoading) ? <CircularProgress size={16} /> : <CreditCardIcon />}
              onClick={handleCardPayment}
              disabled={markingCashPayment || terminalPaymentLoading}
            >
              {terminalPaymentLoading ? t('terminal.waitingForPayment') : t('Card payment')}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={markingCashPayment ? <CircularProgress size={16} /> : <PaymentsIcon />}
              onClick={() => handleMarkCashPayment('cash')}
              disabled={markingCashPayment}
            >
              {t('Cash payment')}
            </Button>
            {amiliEnabled && (
              <HelpButton
                text={t('amili.pdfNotAvailableYet', 'PDF on ladattavissa laskun lähettämisen jälkeen. Amili korvaa tilinumeron ja viitteen laskulla.')}
                title="PDF"
              />
            )}
          </>
        )}

        {(invoice.status === 'sent' || invoice.is_overdue) && (
          <>
            {/* Resend - hide for cash/card payments, EXCEPT credit notes: a cash/card credit
                note is applied without a send step, so this is its (optional) delivery route.
                sent_at distinguishes never-delivered from delivered — apply_credit leaves it
                null on purpose. A delivered e-invoice cannot be resent (immutable — requires
                a credit note), so disable it. */}
            {(!isCashOrCard || invoice.invoice_type === 'INV02') && (
              <Tooltip
                title={invoice.maventa_status === 'delivered'
                  ? t('invoice.alreadyDeliveredUseCreditNote', 'Tämä verkkolasku on jo toimitettu. Tee hyvityslasku uudelleenlähetyksen sijaan.')
                  : ''}
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={openSendDialog}
                    disabled={invoice.maventa_status === 'delivered'}
                  >
                    {invoice.invoice_type === 'INV02'
                      ? t('invoices.sendCreditNote')
                      : (invoice.sent_at ? t('Resend') : t('Send invoice'))}
                  </Button>
                </span>
              </Tooltip>
            )}
            {/* Mark as paid - only for regular invoices, not credit notes, not Amili, and not
                once the invoice has been credited (nothing is owed on it any more). */}
            {invoice.invoice_type !== 'INV02'
              && !invoice.amili_assignment_id
              && !invoice.settlement_status && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={openMarkPaidDialog}
              >
                {t('Mark as paid')}
              </Button>
            )}
            {/* Maksumuistutus. Same predicate as reminder_eligibility() on the backend:
                the org flag is on, the org is not on Amili (Reskontravahti sends its own
                reminders), the customer is a company and something is still open. */}
            {canSendPaymentReminder && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<WarningIcon />}
                onClick={() => setReminderDialogOpen(true)}
              >
                {t('invoices.sendPaymentReminder')}
              </Button>
            )}
            {/* Record that a pending refund has actually been paid back */}
            {invoice.invoice_type === 'INV02'
              && invoice.settlement_status === 'refund_pending' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => setMarkRefundedDialogOpen(true)}
              >
                {t('invoices.markRefunded')}
              </Button>
            )}
          </>
        )}

        {/* "Hyvitä" — commit a credit note and settle it against its original. This is the step
            that takes both documents out of the receivables ledger. Also offered for already-sent
            credit notes so the ones issued before settlement existed can be closed out.
            Bank-transfer credit notes bundle delivery into the same action ("Hyvitä ja lähetä");
            cash/card refunds happen at the till, so their credit note is applied without a send
            step — the customer gets the money back in hand and delivery (PDF/email) is optional. */}
        {invoice.invoice_type === 'INV02'
          && !invoice.settlement_status
          && ['draft', 'sent'].includes(invoice.status) && (
          <Button
            variant="contained"
            color="warning"
            startIcon={applyingCredit ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
            onClick={
              invoice.status === 'draft'
                ? (isCashOrCard
                    ? () => {
                        // Default to the route the money came in on: the usual case is a
                        // refund on the same terminal / from the same till.
                        setRefundMethod(invoice.payment_method);
                        setRestockParts(false);
                        setApplyCreditDialogOpen(true);
                      }
                    : openSendDialog)
                : (restockableLines.length > 0
                    // Nothing to ask except whether the parts go back on the shelf.
                    ? () => {
                        setRestockParts(false);
                        setApplyCreditDialogOpen(true);
                      }
                    : () => handleApplyCredit())
            }
            disabled={applyingCredit}
          >
            {invoice.status === 'draft' && !isCashOrCard
              ? t('invoices.applyCreditAndSend')
              : t('invoices.applyCredit')}
          </Button>
        )}

        {/* Svea financing - for draft invoices, individual customers only */}
        {sveaFinancingEnabled && invoice.customer_type !== 'company' && invoice.status === 'draft' && invoice.invoice_type !== 'INV02' && !invoice.financing_pending && (
          !invoice.svea_payment || !['paid', 'settled', 'delivery_required'].includes(invoice.svea_payment.status)
        ) && (
          <Tooltip title={sveaTooltip} arrow>
            <span>
              <Button
                variant="contained"
                color="info"
                startIcon={sveaLoading ? <CircularProgress size={16} color="inherit" /> : <AccountBalanceIcon />}
                onClick={handleCreateSveaFinancing}
                disabled={sveaLoading || sveaDisabled}
              >
                {t('svea.paymentLink', 'Svea maksulinkki')}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Svea S2S credit decision - for draft invoices, individual customers only */}
        {sveaFinancingEnabled && invoice.customer_type !== 'company' && invoice.status === 'draft' && invoice.invoice_type !== 'INV02' && !invoice.financing_pending && (
          !invoice.svea_payment || !['paid', 'settled', 'delivery_required'].includes(invoice.svea_payment.status)
        ) && (
          <Tooltip title={sveaTooltip} arrow>
            <span>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AccountBalanceIcon />}
                onClick={handleOpenS2sDialog}
                disabled={sveaDisabled}
              >
                {t('svea.creditDecision', 'Svea rahoituspäätös')}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Resurs financing: Resurs texts the customer the link. Individual customers, drafts only. */}
        {resursFinancingEnabled && invoice.customer_type !== 'company' && invoice.status === 'draft' && invoice.invoice_type !== 'INV02'
          && !isPartiallySettled
          && (['created', 'pending'].includes(invoice.resurs_payment?.status) || !invoice.financing_pending) && (
          <Button
            variant="contained"
            color="info"
            startIcon={<AccountBalanceIcon />}
            onClick={() => setResursDialogOpen(true)}
            data-testid="resurs-financing-button"
          >
            {['created', 'pending'].includes(invoice.resurs_payment?.status)
              ? t('resurs.buttonWaiting')
              : t('resurs.button')}
          </Button>
        )}

        {/* Receipt buttons for paid cash/card invoices */}
        {invoice.status === 'paid' && isCashOrCard && (
          <>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPDF}
            >
              {t('Download receipt')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={() => {
                setSendReceiptEmail(invoice?.customer_email || '');
                setSendReceiptDialogOpen(true);
              }}
            >
              {t('Send receipt')}
            </Button>
          </>
        )}

        {/* Combined receipt buttons for split payment invoices */}
        {invoice.has_combined_receipt && (
          <>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCombinedReceipt}
            >
              {t('Download combined receipt')}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<SendIcon />}
              onClick={() => {
                setSendCombinedReceiptEmail(invoice?.customer_email || '');
                setSendCombinedReceiptDialogOpen(true);
              }}
            >
              {t('Send combined receipt')}
            </Button>
          </>
        )}

        {/* Refund button for paid card invoices with terminal, including multi-tender sales */}
        {invoice.status === 'paid' && paymentTerminalEnabled && terminals.length > 0 && canRefundOnTerminal && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={refundLoading ? <CircularProgress size={16} /> : <UndoIcon />}
            onClick={handleRefundClick}
            disabled={refundLoading}
            data-testid="invoice-terminal-refund"
          >
            {refundLoading
              ? (refundProgress ? t('terminal.refundingLeg', refundProgress) : t('terminal.waitingForRefund'))
              : t('terminal.refund')}
          </Button>
        )}

        {/* Refund button for paid Svea financing invoices */}
        {invoice.status === 'paid' && invoice.payment_method === 'financing' && sveaFinancingEnabled &&
          invoice.svea_payment && ['paid', 'settled', 'delivery_required'].includes(invoice.svea_payment.status) && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={sveaRefundLoading ? <CircularProgress size={16} /> : <UndoIcon />}
            onClick={() => setSveaRefundDialogOpen(true)}
            disabled={sveaRefundLoading}
          >
            {sveaRefundLoading ? t('svea.refundProcessing') : t('svea.refund')}
          </Button>
        )}

        {/* Refund button for paid Resurs financing invoices */}
        {invoice.status === 'paid' && invoice.payment_method === 'financing'
          && invoice.resurs_payment?.status === 'paid' && !invoice.resurs_payment?.needs_attention && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={resursRefundLoading ? <CircularProgress size={16} /> : <UndoIcon />}
            onClick={() => setResursRefundDialogOpen(true)}
            disabled={resursRefundLoading}
            data-testid="resurs-refund-button"
          >
            {resursRefundLoading ? t('resurs.refundProcessing') : t('resurs.refund')}
          </Button>
        )}

        {/* Copy as new draft for refunded or cancelled invoices */}
        {(invoice.status === 'refunded' || invoice.status === 'cancelled') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={copyingAsDraft ? <CircularProgress size={16} /> : <ContentCopyIcon />}
            onClick={handleCopyAsDraft}
            disabled={copyingAsDraft}
          >
            {t('invoices.copyAsDraft')}
          </Button>
        )}
      </Box>

      {/* Credit Note Indicators */}
      {invoice.is_credit_note && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('invoices.creditNoteFor')}{' '}
          <Link
            to={`/invoices/${invoice.original_invoice}`}
            style={{ color: 'inherit', fontWeight: 600 }}
          >
            {invoice.original_invoice_number}
          </Link>
          {invoice.status === 'draft' && (
            <Box sx={{ mt: 0.5 }}>{t('invoices.creditNotePartialHint')}</Box>
          )}
        </Alert>
      )}

      {invoice.status === 'draft' && invoice.invoice_type !== 'INV02'
        && parseFloat(invoice.total_incl_vat) < 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('invoices.negativeTotalSendError')}
        </Alert>
      )}

      {invoice.credit_notes_summary?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('invoices.hasCreditNotes')}{' '}
          {invoice.credit_notes_summary.map((cn, index) => (
            <span key={cn.id}>
              {index > 0 && ', '}
              <Link
                to={`/invoices/${cn.id}`}
                style={{ color: 'inherit', fontWeight: 600 }}
              >
                {cn.invoice_number}
              </Link>
            </span>
          ))}
        </Alert>
      )}

      {/* Combined invoice indicators */}
      {invoice.status === 'combined' && invoice.combined_into && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('This invoice has been combined into invoice')}{' '}
          <Link
            to={`/invoices/${invoice.combined_into}`}
            style={{ color: 'inherit', fontWeight: 600 }}
          >
            #{invoice.combined_into_invoice_number || invoice.combined_into}
          </Link>
        </Alert>
      )}

      {invoice.combined_from_invoices?.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('Combined invoice, contains invoices')}:{' '}
          {invoice.combined_from_invoices.map((src, index) => (
            <span key={src.id}>
              {index > 0 && ', '}
              <Link
                to={`/invoices/${src.id}`}
                style={{ color: 'inherit', fontWeight: 600 }}
              >
                #{src.invoice_number}
              </Link>
            </span>
          ))}
        </Alert>
      )}

      {/* Related split invoices */}
      {invoice.related_invoices?.length > 0 && (
        <Alert severity="info" icon={<ReceiptIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t('Related invoices from same work order')}:
          </Typography>
          {invoice.related_invoices.map((rel) => (
            <Box key={rel.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              <Link to={`/invoices/${rel.id}`} style={{ color: 'inherit', fontWeight: 600 }}>
                {rel.invoice_number}
              </Link>
              <Typography variant="body2" component="span" color="text.secondary">
                — {rel.customer_name} ({formatCurrency(rel.total_incl_vat)})
              </Typography>
              {rel.invoice_split_type && rel.invoice_split_type.startsWith('payment_') && (
                <Chip
                  size="small"
                  label={t(`splitType.${rel.invoice_split_type}`)}
                  color={rel.payment_method === 'cash' ? 'success' : rel.payment_method === 'card' ? 'info' : 'default'}
                  variant="outlined"
                />
              )}
            </Box>
          ))}
        </Alert>
      )}

      {/* Work order link */}
      {invoice.maintenance && (
        <Alert severity="info" icon={<ReceiptIcon />} sx={{ mb: 2 }}>
          {t('Work order')}:{' '}
          <Link to={`/maintenances/${invoice.maintenance}`} style={{ color: 'inherit', fontWeight: 600 }}>
            #{invoice.maintenance_work_number || invoice.maintenance}
          </Link>
        </Alert>
      )}

      {/* Parts sale (lähete / kassamyynti) back-links */}
      {invoice.parts_sales && invoice.parts_sales.length > 0 && (
        <Alert severity="info" icon={<LocalShippingIcon fontSize="inherit" />} sx={{ mb: 2 }} data-testid="invoice-parts-sales-links">
          {invoice.parts_sales.map((ps, i) => (
            <React.Fragment key={ps.id}>
              {i > 0 && ', '}
              {ps.mode === 'deferred' ? (
                <>
                  {i === 0 && `${t('parts_sales.lahete')}: `}
                  <Link to={`/lahetteet/${ps.id}`} style={{ color: 'inherit', fontWeight: 600 }}>
                    #{ps.number}
                  </Link>
                </>
              ) : (
                <span style={{ fontWeight: 600 }}>
                  {t('parts_sales.counter_sale')} #{ps.number}
                </span>
              )}
            </React.Fragment>
          ))}
        </Alert>
      )}

      {/* Invoice Details */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
        {/* Customer Info */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
            {t('Customer')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {invoice.customer_name}
          </Typography>
          {(invoice.customer_address_street || invoice.customer_address) && (
            <Typography variant="body2" color="text.secondary">
              {invoice.customer_address_street || invoice.customer_address}
            </Typography>
          )}
          {(invoice.customer_address_postal_code || invoice.customer_address_city) && (
            <Typography variant="body2" color="text.secondary">
              {`${invoice.customer_address_postal_code || ''} ${invoice.customer_address_city || ''}`.trim()}
            </Typography>
          )}
          {invoice.customer_email && (
            <Typography variant="body2" color="text.secondary">
              {invoice.customer_email}
            </Typography>
          )}
          {invoice.customer_phone && (
            <Typography variant="body2" color="text.secondary">
              {invoice.customer_phone}
            </Typography>
          )}
          {invoice.customer_business_id && (
            <Typography variant="body2" color="text.secondary">
              {t('Business ID')}: {invoice.customer_business_id}
            </Typography>
          )}
        </Paper>

        {/* Invoice Info */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
            {t('Invoice details')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">{t('Invoice number')}:</Typography>
            <Typography variant="body2">{invoice.invoice_number}</Typography>

            <Typography variant="body2" color="text.secondary">{t('Reference number')}:</Typography>
            <Typography variant="body2" sx={{ ...numFont }}>{invoice.reference_number}</Typography>

            {invoice.buyers_reference && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Buyers reference')}:</Typography>
                <Typography variant="body2">{invoice.buyers_reference}</Typography>
              </>
            )}

            <Typography variant="body2" color="text.secondary">{t('Invoice date')}:</Typography>
            <Typography variant="body2">
              {formatDate(invoice.invoice_date)}
              {invoice.status === 'draft' && !invoice.invoice_date_manually_set && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({t('updates on send')})
                </Typography>
              )}
            </Typography>

            <Typography variant="body2" color="text.secondary">{t('Due date')}:</Typography>
            <Typography variant="body2" sx={{ color: invoice.is_overdue ? 'error.main' : 'text.primary' }}>
              {formatDate(invoice.due_date)}
              {invoice.status === 'draft' && !invoice.due_date_manually_set && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({t('updates on send')})
                </Typography>
              )}
            </Typography>

            {invoice.paid_date && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Paid date')}:</Typography>
                <Typography variant="body2">{formatDate(invoice.paid_date)}</Typography>
              </>
            )}

            {isPartiallyPaid && (
              <>
                <Typography variant="body2" color="text.secondary">{t('invoices.openBalance')}:</Typography>
                <Typography variant="body2" data-testid="invoice-open-balance">
                  {t('invoices.paidAndOpen', {
                    paid: formatCurrency(invoice.amount_paid),
                    remaining: formatCurrency(invoice.open_balance),
                  })}
                </Typography>
              </>
            )}

            {hasPaymentLegs ? (
              <>
                <Typography variant="body2" color="text.secondary">{t('invoices.paymentBreakdown')}:</Typography>
                <Box data-testid="invoice-payment-breakdown" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {paymentLegs.map((leg) => (
                    <Typography key={leg.id} variant="body2">
                      {paymentMethodLabels[leg.payment_method] || leg.payment_method}: {formatCurrency(leg.amount)}
                    </Typography>
                  ))}
                </Box>
              </>
            ) : invoice.status === 'paid' && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Payment method')}:</Typography>
                <Typography variant="body2">{paymentMethodLabels[invoice.payment_method] || invoice.payment_method}</Typography>
              </>
            )}

            {invoice.payment_method === 'financing' && invoice.financing_reference && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Financing reference')}:</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{invoice.financing_reference}</Typography>
              </>
            )}

            {invoice.payment_method === 'invoiced_elsewhere' && invoice.external_billing_reference && (
              <>
                <Typography variant="body2" color="text.secondary">{t('invoices.invoicedElsewhereReference')}:</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }} data-testid="invoiced-elsewhere-reference">
                  {invoice.external_billing_reference}
                </Typography>
              </>
            )}

            {invoice.vehicle_registration && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Vehicle')}:</Typography>
                <Typography variant="body2">{invoice.vehicle_registration}</Typography>
              </>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Card payment details (POS terminal) */}
      {cardDetailRows.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CreditCardIcon color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t('terminal.cardPaymentDetails')}
            </Typography>
          </Box>

          {/* One block per card leg. Two cards mean two charges on the customer's
              statement, each with its own masked PAN and authorisation code. */}
          {cardDetailRows.map((row, index) => (
            <React.Fragment key={row.key}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Box data-testid="invoice-card-transaction">
                {cardDetailRows.length > 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Chip
                      size="small"
                      label={t('invoices.cardIndex', { index: index + 1, total: cardDetailRows.length })}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(row.amount)}
                    </Typography>
                  </Box>
                )}

                {row.txn ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' }, columnGap: 3, rowGap: 1 }}>
                    {cardDetailRows.length === 1 && row.amount != null && (
                      <>
                        <Typography variant="body2" color="text.secondary">{t('invoices.cardChargedAmount')}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }} data-testid="invoice-card-leg-amount">
                          {formatCurrency(row.amount)}
                        </Typography>
                      </>
                    )}

                    <Typography variant="body2" color="text.secondary">{t('terminal.card')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {[row.txn.card_scheme, formatMaskedCard(row.txn.card_number_masked)].filter(Boolean).join(' ')}
                    </Typography>

                    {row.txn.authorization_code && (
                      <>
                        <Typography variant="body2" color="text.secondary">{t('terminal.authCode')}:</Typography>
                        <Typography variant="body2" sx={{ ...numFont }}>{row.txn.authorization_code}</Typography>
                      </>
                    )}

                    {row.txn.card_entry_mode && (
                      <>
                        <Typography variant="body2" color="text.secondary">{t('terminal.entryModeLabel')}:</Typography>
                        <Typography variant="body2">
                          {CARD_ENTRY_MODE_KEYS[row.txn.card_entry_mode]
                            ? t(CARD_ENTRY_MODE_KEYS[row.txn.card_entry_mode])
                            : row.txn.card_entry_mode}
                          {row.txn.cardholder_verification && row.txn.cardholder_verification !== 'NONE'
                            && ` · ${CARD_CVM_KEYS[row.txn.cardholder_verification]
                              ? t(CARD_CVM_KEYS[row.txn.cardholder_verification])
                              : row.txn.cardholder_verification}`}
                        </Typography>
                      </>
                    )}

                    {row.txn.application_name && (
                      <>
                        <Typography variant="body2" color="text.secondary">{t('terminal.application')}:</Typography>
                        <Typography variant="body2">
                          {row.txn.application_name}
                          {row.txn.application_identifier && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, ...numFont }}>
                              ({row.txn.application_identifier})
                            </Typography>
                          )}
                        </Typography>
                      </>
                    )}

                    {row.txn.acquirer_reference && (
                      <>
                        <Typography variant="body2" color="text.secondary">{t('terminal.reference')}:</Typography>
                        <Typography variant="body2" sx={{ ...numFont }}>{row.txn.acquirer_reference}</Typography>
                      </>
                    )}

                    {(row.txn.acquirer_terminal_id || row.txn.acquirer_timestamp) && (
                      <>
                        <Typography variant="body2" color="text.secondary">{t('terminal.terminalLabel')}:</Typography>
                        <Typography variant="body2">
                          {[row.txn.acquirer_terminal_id, formatCardTimestamp(row.txn.acquirer_timestamp)].filter(Boolean).join(' · ')}
                        </Typography>
                      </>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('invoices.manualCardTender')}
                  </Typography>
                )}
              </Box>
            </React.Fragment>
          ))}
        </Paper>
      )}

      {/* Insurance Details - Show when any insurance data is present */}
      {(invoice.insurance_claim_number || invoice.insurance_damage_date || invoice.insurance_damage_type ||
        invoice.deductible_amount || invoice.policyholder_info || invoice.seller_site_code ||
        ['insurance', 'deductible', 'vat_company'].includes(invoice.invoice_split_type)) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <HealthAndSafetyIcon color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t('Insurance details')}
            </Typography>
            {invoice.invoice_split_type && invoice.invoice_split_type !== 'normal' && !invoice.invoice_split_type.startsWith('payment_') && (
              <Chip size="small" label={t(`splitType.${invoice.invoice_split_type}`)} color="info" variant="outlined" />
            )}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
            {invoice.insurance_claim_number && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Insurance claim number')}:</Typography>
                <Typography variant="body2">{invoice.insurance_claim_number}</Typography>
              </>
            )}
            {invoice.insurance_damage_date && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Damage date')}:</Typography>
                <Typography variant="body2">{formatDate(invoice.insurance_damage_date)}</Typography>
              </>
            )}
            {invoice.insurance_damage_type && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Damage type')}:</Typography>
                <Typography variant="body2">{t(`damageType.${invoice.insurance_damage_type}`)}</Typography>
              </>
            )}
            {invoice.deductible_amount && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Deductible amount')}:</Typography>
                <Typography variant="body2">{formatCurrency(invoice.deductible_amount)}</Typography>
              </>
            )}
            {invoice.insurance_inspector_name && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Inspector name')}:</Typography>
                <Typography variant="body2">{invoice.insurance_inspector_name}</Typography>
              </>
            )}
            {invoice.insurance_inspector_code && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Inspector code')}:</Typography>
                <Typography variant="body2">{invoice.insurance_inspector_code}</Typography>
              </>
            )}
            {invoice.insurance_inspection_date && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Inspection date')}:</Typography>
                <Typography variant="body2">{formatDate(invoice.insurance_inspection_date)}</Typography>
              </>
            )}
            {invoice.repair_start_date && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Repair start date')}:</Typography>
                <Typography variant="body2">{formatDate(invoice.repair_start_date)}</Typography>
              </>
            )}
            {invoice.repair_end_date && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Repair end date')}:</Typography>
                <Typography variant="body2">{formatDate(invoice.repair_end_date)}</Typography>
              </>
            )}
            {invoice.policyholder_info && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Policyholder info')}:</Typography>
                <Typography variant="body2">{invoice.policyholder_info}</Typography>
              </>
            )}
            {invoice.seller_site_code && (
              <>
                <Typography variant="body2" color="text.secondary">{t('Seller site code')}:</Typography>
                <Typography variant="body2">{invoice.seller_site_code}</Typography>
              </>
            )}
          </Box>
        </Paper>
      )}

      {/* Delivery Timeline - show for all sent invoices */}
      {invoice.status !== 'draft' && invoice.delivery_timeline && invoice.delivery_timeline.steps && invoice.delivery_timeline.steps.length > 0 && (
        <InvoiceDeliveryTimeline
          timeline={invoice.delivery_timeline}
          deliveryMethod={invoice.delivery_method}
          maventaId={invoice.maventa_invoice_id}
          errorMessage={invoice.maventa_error_message}
        />
      )}

      {/* Svea Financing Status */}
      {invoice.svea_payment && (
        <FinancingPanel
          testId="svea-panel"
          title={t('svea.title', 'Svea rahoitus')}
          badge={<SveaFinancingStatusBadge status={invoice.svea_payment.status} />}
          subtitle={invoice.svea_payment.payment_method_display
            ? `${invoice.svea_payment.payment_method_display}${invoice.svea_payment.payment_type === 's2s' ? ' (S2S)' : ''}`
            : ''}
          attention={invoice.svea_payment.needs_attention
            && ['paid', 'settled', 'delivery_required'].includes(invoice.svea_payment.status)
            ? t('financing.needsAttention') : ''}
          details={invoice.svea_payment.payment_type === 's2s' ? [
            {
              label: t('svea.monthlyPayment', 'Kuukausierä'),
              value: invoice.svea_payment.monthly_amount ? `${invoice.svea_payment.monthly_amount} €` : '',
            },
            {
              label: t('svea.contractLength', 'Maksuaika'),
              value: invoice.svea_payment.contract_months
                ? `${invoice.svea_payment.contract_months} ${t('svea.months', 'kk')}` : '',
            },
            { label: t('svea.campaign', 'Kampanja'), value: invoice.svea_payment.campaign_description },
          ] : []}
          linkUrl={invoice.svea_payment.payment_url && ['created', 'pending'].includes(invoice.svea_payment.status)
            ? (invoice.svea_payment.customer_url || invoice.svea_payment.payment_url) : ''}
          onCopied={() => setSnackbar({ open: true, message: t('svea.linkCopied', 'Linkki kopioitu'), severity: 'success' })}
          actions={invoice.financing_pending && ['created', 'pending'].includes(invoice.svea_payment.status) ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={handleCancelFinancing}
              disabled={financingCancelLoading}
              startIcon={financingCancelLoading ? <CircularProgress size={14} /> : null}
              data-testid="svea-panel-cancel"
            >
              {t('financing.cancelRequest')}
            </Button>
          ) : null}
        />
      )}

      {/* Resurs Financing Status */}
      <ResursFinancingPanel
        invoice={invoice}
        canManage={can(currentOrganization, 'invoices.manage')}
        onInvoiceUpdated={(data) => setInvoice(data)}
        onNotify={(message, severity) => setSnackbar({ open: true, message, severity })}
        onRefund={() => setResursRefundDialogOpen(true)}
      />

      {/* Amili Payment Status - only show if invoice has amili tracking */}
      {invoice.amili_payment_status && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <ReceiptIcon color="secondary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.main' }}>
              {invoice.invoice_type === 'INV02'
                ? t('amili.creditNoteSection')
                : t('amili.paymentTracking', 'Maksuvalvonta')}
            </Typography>
            <AmiliPaymentStatusBadge status={invoice.amili_payment_status} />
            {/* One help for the whole section instead of one per button: a row of buttons each
                trailed by its own ? read as a jumble. The dialogs repeat what matters. */}
            {invoice.amili_assignment_id && invoice.invoice_type !== 'INV02' && (
              <HelpButton
                title={t('amili.paymentTracking', 'Maksuvalvonta')}
                maxWidth={420}
                sx={{ ml: -1.5 }}
                testId="amili-help"
                text={[
                  ['amili.markExternalPayment', 'amili.externalPaymentDescription'],
                  ['amili.freeze', 'amili.freezeHelp'],
                  ['amili.cancelCollection', 'amili.cancelHelp'],
                  ['amili.messagesTitle', 'amili.messagesHelp'],
                ].map(([label, help]) => (
                  <Box component="span" key={label} sx={{ display: 'block', mb: 1.5, '&:last-child': { mb: 0 } }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 600, color: 'text.primary' }}>
                      {t(label)}
                    </Box>
                    {t(help)}
                  </Box>
                ))}
              />
            )}
          </Box>

          {/* A credit note is passed to Amili like any other document — Amili Auto assigns
              everything sent through the org's Maventa account and there is no per-invoice
              opt-out. But nothing is being collected on it, so the collection wording would
              be wrong. Show what is actually true instead. */}
          {/* Settled here, still being collected by Amili. The sync raises this when the
              booked payments at Amili fall short of the total while the invoice is paid or
              credited in Verstaani (invoice 20260318: an 18,08 € ohisuoritus on 344,83 €,
              a maksuvaatimus to the customer a month later). Nothing clears it but Amili
              closing the assignment, so the only way out is to sort it out with Amili. */}
          {invoice.amili_conflict_at && invoice.amili_conflict_kind === 'paid_on_credited' && (
            /* Credited in full, collection called off, and the customer paid the old invoice to
               Amili anyway (Vuorelan Autofit 20260101/20260102, Menix 20260084, 2026-10). Nobody
               is collecting: Amili settles the money and the invoice stays credited, so the
               workshop refunds it or books it on the replacement invoice. */
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="amili-paid-on-credited-alert">
              <AlertTitle>{t('amili.conflictPaidOnCreditedTitle')}</AlertTitle>
              {t('amili.conflictPaidOnCreditedInfo', {
                amount: invoice.amili_booked_total != null ? formatCurrency(invoice.amili_booked_total) : '',
                date: formatDate(invoice.amili_booked_on || invoice.amili_conflict_at),
              })}
            </Alert>
          )}
          {invoice.amili_conflict_at && invoice.amili_conflict_kind !== 'paid_on_credited' && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="amili-conflict-alert">
              <AlertTitle>{t('amili.conflictTitle')}</AlertTitle>
              {t(
                invoice.settlement_status === 'credited' ? 'amili.conflictCreditedInfo' : 'amili.conflictPaidInfo',
                { date: formatDate(invoice.amili_conflict_at) },
              )}
            </Alert>
          )}
          {invoice.invoice_type === 'INV02' ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('amili.creditNoteInfo')}
            </Alert>
          ) : (
            <>
              {invoice.amili_payment_status === 'pending_sync' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('amili.pendingSyncInfo', 'Lasku on lähetetty Maventan kautta. Maksuvalvonnan käynnistymistä odotetaan.')}
                </Alert>
              )}
              {/* Two different things used to share the not_tracked wording: an invoice
                  Amili never received, and one whose assignment was called off afterwards.
                  Telling a workshop "Amili ei ole vastaanottanut laskua" about a collection
                  they themselves cancelled is simply untrue, so the assignment id decides.
                  Legacy rows still carry not_tracked with an assignment id. */}
              {(invoice.amili_payment_status === 'cancelled'
                || (invoice.amili_payment_status === 'not_tracked' && invoice.amili_assignment_id)) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('amili.cancelledInfo', 'Tämän laskun maksuvalvonta on päättynyt: toimeksianto on peruttu Amilissa. Merkitse lasku tarvittaessa maksetuksi käsin.')}
                </Alert>
              )}
              {invoice.amili_payment_status === 'not_tracked' && !invoice.amili_assignment_id && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('amili.notTrackedWarning', 'Maksuvalvonta ei käynnistynyt tälle laskulle. Amili ei ole vastaanottanut laskua.')}
                </Alert>
              )}
              {!['pending_sync', 'not_tracked', 'cancelled'].includes(invoice.amili_payment_status)
                && !invoice.amili_conflict_at && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('amili.trackingInfo', 'Tämän laskun maksuvalvonta on automaattisesti käytössä Amili Kassavirta -palvelun kautta. Maksumuistutukset ja perintä hoidetaan automaattisesti.')}
                </Alert>
              )}
            </>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {invoice.amili_assignment_id && (
              <Box>
                <Typography variant="body2" color="text.secondary">{t('amili.assignmentId', 'Toimeksianto')}</Typography>
                <Typography variant="body2" sx={{ ...numFont, fontSize: '12px' }}>
                  {invoice.amili_assignment_id}
                </Typography>
              </Box>
            )}
            {invoice.amili_paid_at && (
              <Box>
                <Typography variant="body2" color="text.secondary">{t('amili.paidAt', 'Maksettu')}</Typography>
                <Typography variant="body2">
                  {new Date(invoice.amili_paid_at).toLocaleString('fi-FI')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Event timeline */}
          {invoice.amili_events && invoice.amili_events.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                {t('amili.events', 'Tapahtumat')}
              </Typography>
              {invoice.amili_events.map((event, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'baseline' }}>
                  <Typography variant="caption" color="text.disabled" sx={{ minWidth: 80 }}>
                    {event.created_at ? new Date(event.created_at).toLocaleDateString('fi-FI') : ''}
                  </Typography>
                  <Typography variant="body2">
                    {describeAmiliEvent(event)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* One action row: ohisuoritus, Keskeytä / Peru perintä. Then the message thread. */}
          <AmiliActions
            invoice={invoice}
            monitored={invoice.invoice_type !== 'INV02' && isAmiliMonitored(invoice)}
            canManage={can(currentOrganization, 'invoices.mark_paid')}
            onInvoiceUpdated={(data) => setInvoice(data)}
            onNotify={(message, severity) => setSnackbar({ open: true, message, severity })}
            paymentActions={(showOhisuoritusButton || showOhisuoritusConflictButton) && (
              <>
                {showOhisuoritusButton && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => openOhisuoritusDialog(false)}
                    data-testid="ohisuoritus-button"
                  >
                    {t('amili.markExternalPayment', 'Merkitse maksetuksi (ohisuoritus)')}
                  </Button>
                )}
                {showOhisuoritusConflictButton && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => openOhisuoritusDialog(true)}
                    data-testid="ohisuoritus-conflict-button"
                  >
                    {t('amili.reportConflictPayment')}
                  </Button>
                )}
              </>
            )}
          />
          <AmiliMessages invoice={invoice} canSend={can(currentOrganization, 'invoices.mark_paid')} />
        </Paper>
      )}

      {/* Payment ledger: every payment recorded on the invoice, reversals included */}
      {showPaymentsTable && (
        <Paper sx={{ p: 3, mb: 3 }} data-testid="invoice-payments">
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('invoices.paymentsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('invoices.paidAndOpen', {
              paid: formatCurrency(invoice.amount_paid),
              remaining: formatCurrency(invoice.open_balance),
            })}
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('invoices.paymentDate')}</TableCell>
                  <TableCell>{t('Payment method')}</TableCell>
                  <TableCell>{t('invoices.paymentSource')}</TableCell>
                  <TableCell align="right">{t('invoices.paymentAmount')}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {ledgerRows.map((row) => (
                  <TableRow key={row.id} data-testid={`invoice-payment-row-${row.id}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.payment_date)}</TableCell>
                    <TableCell>{paymentMethodLabels[row.payment_method] || row.payment_method}</TableCell>
                    <TableCell>
                      {t(`invoices.source.${row.source}`, row.source)}
                      {row.reverses ? ` (${t('invoices.reversed')})` : ''}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        whiteSpace: 'nowrap',
                        color: parseFloat(row.amount) < 0 ? 'error.main' : 'inherit',
                        textDecoration: row.refunded_at ? 'line-through' : 'none',
                      }}
                    >
                      {formatCurrency(row.amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {canReverseRow(row) && (
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<UndoIcon fontSize="small" />}
                          onClick={() => setReversePaymentTarget(row)}
                        >
                          {t('invoices.reversePayment')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Accounting integration transfer status (Netvisor etc.) */}
      {invoice.accounting_transfers?.length > 0 && (() => {
        // Show the latest transfer; retries update it in place (attempts), but a
        // re-send can create a newer row, so pick by id.
        const transfer = [...invoice.accounting_transfers].sort((a, b) => b.id - a.id)[0];
        return (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('accounting.transferTitle', 'Kirjanpitosiirto')}
              </Typography>
              <AccountingTransferBadge
                status={transfer.status}
                errorMessage={transfer.error_message}
                errorCategory={transfer.error_category}
              />
              {transfer.status === 'failed' && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleRetryAccountingTransfer(transfer.id)}
                  disabled={retryingAccountingTransfer}
                  data-testid="accounting-transfer-retry"
                >
                  {retryingAccountingTransfer ? (
                    <CircularProgress size={16} />
                  ) : (
                    t('accounting.retryTransfer', 'Yritä uudelleen')
                  )}
                </Button>
              )}
            </Box>
          </Paper>
        );
      })()}

      {/* Invoice Notes - always shown for easy editing */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
              {t('Invoice notes')}
            </Typography>
            {invoice.notes ? (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {invoice.notes}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                {t('No notes yet')}
              </Typography>
            )}
          </Box>
          {isDraft && (
            <IconButton
              size="small"
              onClick={() => {
                setEditHeaderFocusField('notes');
                setEditHeaderDialogOpen(true);
              }}
              sx={{ ml: 1 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Paper>

      {/* Line Items */}
      <Paper sx={{ mb: 3 }}>
        {/* Reorder affordance. Only offered while the invoice is still a draft — once
            it is sent the customer has the PDF and the line order is part of an
            issued document, so the backend refuses to change it. */}
        {isDraft && (invoice.lines?.length || 0) > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1 }}>
            <Tooltip title={t('Reorder lines')}>
              <IconButton
                size="small"
                data-testid="invoice-reorder-mode-toggle"
                aria-label={t('Reorder lines')}
                aria-pressed={reorderMode}
                onClick={() => setReorderMode((v) => !v)}
                sx={{
                  color: reorderMode ? 'primary.main' : 'text.disabled',
                  border: 1,
                  borderColor: reorderMode ? 'primary.main' : 'divider',
                  borderRadius: '8px',
                }}
              >
                <SwapVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                {reorderMode && isDraft && (invoice.lines?.length || 0) > 1 && (
                  <TableCell sx={{ width: '1%', pl: 2, pr: 0 }} />
                )}
                <TableCell sx={{ fontWeight: 600 }}>{t('Description')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Quantity')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Unit price')}</TableCell>
                {invoice.lines?.some(line => parseFloat(line.discount_percent) > 0) && (
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Discount')}</TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t('VAT %')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Total (incl. VAT)')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.lines?.map((line, idx) => (
                <TableRow
                  key={line.id}
                  onClick={() => handleLineClick(line)}
                  sx={{
                    cursor: isDraft ? 'pointer' : 'default',
                    '&:hover': isDraft ? { bgcolor: 'action.hover' } : {},
                  }}
                >
                  {reorderMode && isDraft && invoice.lines.length > 1 && (
                    <TableCell sx={{ width: '1%', pl: 2, pr: 0, py: 0.5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <IconButton
                          size="small"
                          disabled={idx === 0 || reordering}
                          data-testid={`invoice-line-move-up-${line.id}`}
                          aria-label={t('Move up')}
                          onClick={(e) => { e.stopPropagation(); handleMoveLine(idx, -1); }}
                          sx={{ p: 0.25 }}
                        >
                          <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={idx === invoice.lines.length - 1 || reordering}
                          data-testid={`invoice-line-move-down-${line.id}`}
                          aria-label={t('Move down')}
                          onClick={(e) => { e.stopPropagation(); handleMoveLine(idx, 1); }}
                          sx={{ p: 0.25 }}
                        >
                          <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        {line.description}
                        {line.row_free_text && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {line.row_free_text}
                          </Typography>
                        )}
                        {line.product_code && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {line.product_code}
                          </Typography>
                        )}
                      </Box>
                      {line.part && line.reduce_from_inventory && line.stock_info && (
                        <Tooltip title={t('Will be deducted from inventory')}>
                          <InventoryIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.6 }} />
                        </Tooltip>
                      )}
                      {isDraft && (
                        <EditIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.5 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {line.quantity} {line.unit}
                  </TableCell>
                  <TableCell align="right">
                    {line.is_margin_vat_scheme
                      ? formatCurrency(line.unit_price_incl_vat || (parseFloat(line.line_total_incl_vat) / parseFloat(line.quantity)))
                      : formatCurrency(line.unit_price_excl_vat)}
                  </TableCell>
                  {invoice.lines?.some(l => parseFloat(l.discount_percent) > 0) && (
                    <TableCell align="right">
                      {parseFloat(line.discount_percent) > 0 ? `${parseFloat(line.discount_percent).toFixed(1)}%` : ''}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {line.is_margin_vat_scheme ? (
                      <Tooltip title={t('Margin VAT scheme - VAT calculated on profit margin')}>
                        <span>—</span>
                      </Tooltip>
                    ) : (
                      `${line.vat_rate}%`
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: parseFloat(line.line_total_incl_vat) < 0 ? 'error.main' : undefined }}
                  >
                    {formatCurrency(line.line_total_incl_vat)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals */}
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ minWidth: 280 }}>
            {(() => {
              const hasMarginItems = invoice.lines?.some(l => l.is_margin_vat_scheme);
              const marginTotal = hasMarginItems
                ? invoice.lines.reduce((sum, line) => line.is_margin_vat_scheme ? sum + parseFloat(line.line_total_incl_vat || 0) : sum, 0)
                : 0;
              const normalExclVat = invoice.lines?.reduce((sum, line) => line.is_margin_vat_scheme ? sum : sum + parseFloat(line.line_total_excl_vat || 0), 0) || 0;
              const normalVat = invoice.lines?.reduce((sum, line) => line.is_margin_vat_scheme ? sum : sum + parseFloat(line.vat_amount || 0), 0) || 0;
              const totalInclVat = invoice.lines?.reduce((sum, line) => sum + parseFloat(line.line_total_incl_vat || 0), 0) || 0;

              return (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography color="text.secondary">{t('Subtotal (VAT 0)')}:</Typography>
                    <Typography>{formatCurrency(normalExclVat)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography color="text.secondary">{t('VAT')}:</Typography>
                    <Typography>{formatCurrency(normalVat)}</Typography>
                  </Box>
                  {hasMarginItems && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography color="text.secondary">{t('Margin taxation')}:</Typography>
                      <Typography>{formatCurrency(marginTotal)}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 600 }}>{t('Total (incl. VAT)')}:</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                      {formatCurrency(totalInclVat)}
                    </Typography>
                  </Box>
                </>
              );
            })()}
          </Box>
        </Box>

        {/* Add Line Button - only for drafts */}
        {isDraft && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setAddLineDialogOpen(true)}
              sx={{ borderRadius: '8px' }}
            >
              {t('Add line')}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Payment Info. Never on a credit note: it would tell the customer to pay a NEGATIVE
          amount into our account. The PDF already suppresses the bank barcode for INV02. */}
      {invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.status !== 'combined'
        && invoice.invoice_type !== 'INV02' && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#eff6ff' }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
            {t('Payment information')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">{t('Beneficiary')}</Typography>
              <Typography variant="body1">{invoice.org_name}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{t('IBAN')}</Typography>
              <Typography variant="body1" sx={{ ...numFont }}>{invoice.org_iban}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{t('Reference number')}</Typography>
              <Typography variant="body1" sx={{ ...numFont, fontWeight: 600 }}>
                {invoice.reference_number}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">{t('Amount')}</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatCurrency(invoice.total_incl_vat)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Attachments (liitteet). Renders nothing until the invoice actually has
          one; the first is added from the Actions menu via attachmentsRef. */}
      <InvoiceAttachmentsPanel
        ref={attachmentsRef}
        invoiceId={invoice.id}
        organizationId={currentOrganization?.organization_id}
        invoiceStatus={invoice.status}
        maintenanceId={invoice.maintenance}
        onSummaryChange={setAttachmentSummary}
        onNotify={(message, severity) => setSnackbar({ open: true, message, severity })}
      />

      {/* Internal Notes - invoice.notes is shown above line items */}
      {invoice.internal_notes && (
        <Paper sx={{ p: 3 }}>
          <Box>
            <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1, fontWeight: 600 }}>
              {t('Internal notes')}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {invoice.internal_notes}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Send Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isCreditingDraft ? t('invoices.applyCreditAndSend') : t('Send invoice')}</DialogTitle>
        <DialogContent>
          {/* Crediting an invoice Amili is still collecting stops that collection. Say so
              before the click: the cancel goes out as part of "Hyvitä". */}
          {isCreditingDraft && invoice?.original_invoice_amili_monitored && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="amili-credit-cancel-note">
              {t('amili.creditCancelsCollection')}
            </Alert>
          )}
          {/* "Palauta osat varastoon" rides along with "Hyvitä ja lähetä": the credit is
              applied in this same action, so this is the only moment to ask. */}
          {isCreditingDraft && renderRestockChoice({ mb: 2 })}
          {/* Amili-specific alerts */}
          {isAmili && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('amili.sendBanner', 'Maksuvalvonta on käytössä. Kaikki lähetetyt laskut ovat automaattisesti maksuvalvonnassa.')}
              </Alert>

              {(() => {
                const amiliActivatedAt = currentOrganization?.amili_activated_at;
                if (!amiliActivatedAt || invoice?.customer_type === 'company') return null;
                const start = new Date(amiliActivatedAt);
                const today = new Date();
                let businessDays = 0;
                const d = new Date(start);
                while (d < today) {
                  d.setDate(d.getDate() + 1);
                  const day = d.getDay();
                  if (day !== 0 && day !== 6) businessDays++;
                }
                if (businessDays >= 5) return null;
                const remaining = 5 - businessDays;
                return (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('amili.gracePeriodWarning', 'Amili Kassavirta on aktivoitu alle 5 arkipäivää sitten. Kuluttajalaskuille on 5 arkipäivän karenssi, jona aikana maksuvalvonta ei vielä käynnisty. Karenssi päättyy {{days}} arkipäivän kuluttua.', { days: remaining })}
                  </Alert>
                );
              })()}
            </>
          )}

          {/* Pending KYC alert for non-Amili mode */}
          {!isAmili && currentOrganization?.amili_status === 'pending' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('amili.pendingKycSendWarning', 'Amili Kassavirta -aktivointi on kesken. Maksuvalvonta, maksumuistutukset ja perintä eivät ole vielä käytössä tälle laskulle. Viimeistele aktivointi asetuksissa.')}
            </Alert>
          )}

          {/* €0 line items warning */}
          {(() => {
            const zeroLines = (invoice?.lines || []).filter(l =>
              parseFloat(l.unit_price_excl_vat || 0) === 0
            );
            return zeroLines.length > 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                  {t('invoice.zeroPriceLinesWarning', {
                    defaultValue: 'Hinnoittelematta jääneet rivit ({{count}}):',
                    count: zeroLines.length,
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {zeroLines.map(l => l.description).join(', ')}
                </Typography>
              </Alert>
            ) : null;
          })()}

          {/* Missing claim number warning for insurance invoices */}
          {invoice?.invoice_split_type === 'insurance' && !invoice?.insurance_claim_number && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('claimNumberMissingWarning')}
            </Alert>
          )}

          {/* Delivery method cards */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('Delivery method')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {[
              // Email option: SMTP (free) for non-Amili, Maventa (paid) for Amili
              ...(isAmili
                ? [{ value: 'email_maventa', label: t('amili.emailMaventa', 'Sähköposti (Maventa)'), price: '0,50 €' }]
                : [{ value: 'email', label: t('Email'), price: t('free', 'Ilmainen') }]
              ),
              // Verkkolasku: only for company customers with Maventa enabled
              ...(maventaEnabled && invoice?.customer_type === 'company'
                ? [{ value: 'verkkolasku', label: t('E-Invoice'), price: '0,50 €', disabled: !einvoiceEnabled }]
                : []
              ),
              // Paper options: only if Maventa enabled
              ...(maventaEnabled
                ? [
                    { value: 'paper_economy', label: t('amili.paperEconomy', 'Paperilasku Economy'), price: '3,00 €' },
                    { value: 'paper_priority', label: t('amili.paperPriority', 'Paperilasku Priority'), price: '5,00 €' },
                  ]
                : []
              ),
            ].map((option) => (
              <Paper
                key={option.value}
                variant="outlined"
                onClick={() => !option.disabled && setDeliveryMethod(option.value)}
                sx={{
                  p: 1.5,
                  cursor: option.disabled ? 'not-allowed' : 'pointer',
                  opacity: option.disabled ? 0.5 : 1,
                  borderColor: deliveryMethod === option.value ? 'primary.main' : 'divider',
                  borderWidth: deliveryMethod === option.value ? 2 : 1,
                  bgcolor: deliveryMethod === option.value ? 'primary.50' : 'background.paper',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: deliveryMethod === option.value ? 600 : 400 }}>
                  {option.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.price}
                </Typography>
              </Paper>
            ))}
          </Box>

          {/* Attachments: what actually happens to them depends on the route, and
              the differences are not guessable, so spell them out before sending. */}
          {attachmentSummary.count > 0 && (() => {
            const count = attachmentSummary.count;
            const isMaventaRoute = deliveryMethod === 'verkkolasku' || deliveryMethod === 'email_maventa';
            const isPaperRoute = deliveryMethod === 'paper_priority' || deliveryMethod === 'paper_economy';

            if (isPaperRoute) {
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('invoiceAttachments.paperWarning', {
                    defaultValue: 'Laskulla on {{count}} liitettä, mutta liitteitä ei tulosteta paperilaskuun. Valitse toinen toimitustapa tai toimita liitteet erikseen.',
                    count,
                  })}
                </Alert>
              );
            }

            if (isMaventaRoute && attachmentSummary.totalBytes > MAX_MAVENTA_ATTACHMENT_BYTES) {
              return (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t('invoiceAttachments.tooLargeForEinvoice', {
                    defaultValue: 'Liitteet ovat liian suuret verkkolaskun mukana lähetettäväksi ({{size}} Mt, enintään {{max}} Mt). Poista liitteitä tai lähetä lasku sähköpostitse.',
                    size: (attachmentSummary.totalBytes / 1024 / 1024).toFixed(1),
                    max: MAX_MAVENTA_ATTACHMENT_BYTES / 1024 / 1024,
                  })}
                </Alert>
              );
            }

            if (deliveryMethod === 'verkkolasku') {
              // Peppol requires attachments to be embedded inside the XML. We send
              // Finvoice with the files in a ZIP, and Maventa converts the XML for
              // Peppol delivery — the attachments are not guaranteed to survive
              // that conversion, so say so rather than let it fail silently.
              if ((verkkolaskuOperator || '').toUpperCase().includes('PEPPOL')) {
                return (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {t(
                      'invoiceAttachments.peppolWarning',
                      'Vastaanottaja on Peppol-verkossa. Liitteet eivät välttämättä välity Peppol-laskun mukana. Varmista liitteiden perillemeno tai toimita ne erikseen.'
                    )}
                  </Alert>
                );
              }
              return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('invoiceAttachments.verkkolaskuInfo', {
                    defaultValue: '{{count}} liitettä lähetetään verkkolaskun mukana. Pankkiverkon kautta toimitettavissa laskuissa vastaanottaja saa liitteet linkkinä laskun katselupalvelussa.',
                    count,
                  })}
                </Alert>
              );
            }

            if (deliveryMethod === 'email_maventa') {
              return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('invoiceAttachments.emailMaventaInfo', {
                    defaultValue: '{{count}} liitettä toimitetaan sähköpostin mukana. Maventan asetuksista riippuen vastaanottaja saa ne joko liitetiedostoina tai latauslinkkinä.',
                    count,
                  })}
                </Alert>
              );
            }

            return (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('invoiceAttachments.emailInfo', {
                  defaultValue: '{{count}} liitettä toimitetaan sähköpostissa latauslinkillä. Linkki on voimassa 90 päivää.',
                  count,
                })}
              </Alert>
            );
          })()}

          {buyerNameWarning}

          {/* Recipient postal address must be complete or Maventa accepts then bounces the invoice */}
          {deliveryNeedsPostalAddress && recipientAddressIncomplete && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(
                'invoices.recipientAddressIncomplete',
                'Asiakkaan osoitetiedot (katuosoite, postinumero, postitoimipaikka) ovat puutteelliset. Laskua ei voida toimittaa tällä toimitustavalla ilman täydellistä osoitetta.'
              )}
              {invoice?.customer_id && (
                <Button
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => {
                    setSendDialogOpen(false);
                    openEditCustomerDialog();
                  }}
                >
                  {t('invoices.editCustomerInfo', 'Muokkaa asiakkaan tietoja')}
                </Button>
              )}
            </Alert>
          )}

          {/* Our own bank details must be complete and consistent or the recipient's bank
              rejects the e-invoice after Maventa has already reported it delivered */}
          {senderBankBlocksEinvoice && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {senderBankError.code === 'iban_missing'
                ? t(
                    'invoices.senderIbanMissing',
                    'Yrityksen tilinumero (IBAN) puuttuu. Lisää pankkitiedot laskutusasetuksiin ennen verkkolaskun lähettämistä.'
                  )
                : senderBankError.code === 'bic_mismatch'
                  ? t('invoices.senderBicMismatch', {
                      defaultValue:
                        'Yrityksen BIC-koodi ei vastaa tilinumeroa. Vastaanottajan pankki hylkää verkkolaskun. IBANin perusteella BIC-koodin pitäisi olla {{expectedBic}}. Korjaa pankkitiedot laskutusasetuksissa.',
                      expectedBic: senderBankError.expected_bic,
                    })
                  : senderBankError.expected_bic
                    ? t('invoices.senderBicMissingWithHint', {
                        defaultValue:
                          'Yrityksen BIC-koodi puuttuu. Vastaanottajan pankki hylkää verkkolaskun ilman sitä. Lisää BIC-koodi {{expectedBic}} laskutusasetuksiin.',
                        expectedBic: senderBankError.expected_bic,
                      })
                    : t(
                        'invoices.senderBicMissing',
                        'Yrityksen BIC-koodi puuttuu. Vastaanottajan pankki hylkää verkkolaskun ilman sitä. Lisää BIC-koodi laskutusasetuksiin.'
                      )}
              <Button
                size="small"
                sx={{ ml: 1 }}
                onClick={() => {
                  setSendDialogOpen(false);
                  navigate('/settings?tab=invoicing');
                }}
              >
                {t('invoices.openInvoicingSettings', 'Avaa laskutusasetukset')}
              </Button>
            </Alert>
          )}

          {/* E-invoice availability alerts */}
          {maventaEnabled && invoice?.customer_type === 'company' && !einvoiceEnabled && !einvoicingAvailable && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('einvoicing.testingPhaseDescription', 'E-invoicing (verkkolaskutus) is currently being tested and is not yet available for production use. We will notify you when the service is ready.')}
            </Alert>
          )}
          {maventaEnabled && invoice?.customer_type === 'company' && !einvoiceEnabled && einvoicingAvailable && isTrial && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('E-invoicing is available with a paid subscription. Upgrade to send electronic invoices through the Finnish banking network.')}
            </Alert>
          )}

          {/* Email fields (non-Amili SMTP) */}
          {deliveryMethod === 'email' && (
            <>
              <TextField
                autoFocus
                margin="dense"
                label={t('Email')}
                type="email"
                fullWidth
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox checked={ccAccountant} onChange={(e) => setCcAccountant(e.target.checked)} />
                }
                label={t('CC accountant')}
              />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />
                  }
                  label={t('Attach invoice PDF')}
                />
                <HelpButton
                  text={t('attachPdfHelp')}
                />
              </Box>
            </>
          )}

          {/* Email field for Amili email_maventa */}
          {deliveryMethod === 'email_maventa' && (
            <TextField
              autoFocus
              margin="dense"
              label={t('Email')}
              type="email"
              fullWidth
              value={amiliSendEmail}
              onChange={(e) => setAmiliSendEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          {/* Verkkolasku fields */}
          {deliveryMethod === 'verkkolasku' && (
            <>
              {customerHasVerkkolaskuAddress && invoice?.maventa_status !== 'failed' && !editingVerkkolaskuAddress ? (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'success.50' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 600 }}>
                      {t('E-Invoice Recipient')}
                    </Typography>
                    <IconButton size="small" onClick={() => setEditingVerkkolaskuAddress(true)}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {invoice?.customer_name}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('E-Invoice Address')}</Typography>
                      <Typography variant="body2" sx={{ ...numFont }}>{verkkolaskuAddress}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('Operator Code')}</Typography>
                      <Typography variant="body2" sx={{ ...numFont }}>{verkkolaskuOperator}</Typography>
                    </Box>
                  </Box>
                </Paper>
              ) : (
                <>
                  <TextField
                    autoFocus
                    margin="dense"
                    label={t('E-Invoice Address')}
                    fullWidth
                    value={verkkolaskuAddress}
                    onChange={(e) => setVerkkolaskuAddress(e.target.value.toUpperCase())}
                    placeholder="003712345678 / FI37..."
                    error={einvoiceAddressError}
                    helperText={einvoiceAddressError ? t('Invalid e-invoice address format. Accepted: OVT (0037...), IBAN, or 5-30 alphanumeric characters.') : ''}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    margin="dense"
                    label={t('Operator Code')}
                    fullWidth
                    value={verkkolaskuOperator}
                    onChange={(e) => setVerkkolaskuOperator(e.target.value.toUpperCase())}
                    placeholder="DABAFIHH"
                    error={operatorCodeError}
                    helperText={operatorCodeError ? t('Invalid operator code. Must be 4-15 alphanumeric characters.') : ''}
                    sx={{ mb: 2 }}
                  />
                  {verkkolaskuAddress && verkkolaskuOperator && (
                    <FormControlLabel
                      control={<Checkbox checked={saveToCustomer} onChange={(e) => setSaveToCustomer(e.target.checked)} />}
                      label={t('Save e-invoice address to customer')}
                      sx={{ mb: 1 }}
                    />
                  )}
                </>
              )}

              {/* Insurance invoice fields */}
              {invoice?.invoice_split_type === 'insurance' || invoice?.invoice_split_type === 'deductible' ? (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    margin="dense"
                    label={t('Claim Number')}
                    fullWidth
                    value={insuranceClaimNumber}
                    onChange={(e) => setInsuranceClaimNumber(e.target.value)}
                    placeholder={t('Vahinkonumero')}
                    sx={{ mb: 2 }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                    <DatePicker
                      label={t('Damage Date')}
                      value={dateStringToDate(insuranceDamageDate)}
                      onChange={(newValue) => setInsuranceDamageDate(dateToDateString(newValue))}
                      format="dd.MM.yyyy"
                      slotProps={{ textField: { margin: 'dense', fullWidth: true } }}
                    />
                  </LocalizationProvider>
                  {(() => {
                    const warnings = [];
                    if (!insuranceClaimNumber) warnings.push(t('Insurance claim number missing'));
                    if (!insuranceDamageDate) warnings.push(t('Damage date missing'));
                    if (!currentOrganization?.insurance_seller_site_code) warnings.push(t('Workshop identifier missing'));
                    if (!invoice?.vehicle_registration) warnings.push(t('Registration number missing'));
                    if (warnings.length === 0) return null;
                    return (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {t('Insurance invoice warnings')}
                        </Typography>
                        {warnings.map((w, i) => (
                          <Typography key={i} variant="body2">• {w}</Typography>
                        ))}
                      </Alert>
                    );
                  })()}
                </Box>
              ) : (
                <Accordion
                  disableGutters
                  elevation={0}
                  variant="outlined"
                  sx={{
                    mt: 2,
                    borderRadius: 1,
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: 'auto',
                      '& .MuiAccordionSummary-content': { my: 1 },
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t('Insurance invoice fields (optional)')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <TextField
                      margin="dense"
                      label={t('Claim Number')}
                      fullWidth
                      value={insuranceClaimNumber}
                      onChange={(e) => setInsuranceClaimNumber(e.target.value)}
                      placeholder={t('Vahinkonumero')}
                      sx={{ mb: 2 }}
                    />
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                      <DatePicker
                        label={t('Damage Date')}
                        value={dateStringToDate(insuranceDamageDate)}
                        onChange={(newValue) => setInsuranceDamageDate(dateToDateString(newValue))}
                        format="dd.MM.yyyy"
                        slotProps={{
                          textField: { margin: 'dense', fullWidth: true },
                        }}
                      />
                    </LocalizationProvider>
                  </AccordionDetails>
                </Accordion>
              )}

              <Alert severity="info" sx={{ mt: 1 }}>
                {t('Invoice will be sent electronically through the Finnish bank network')}
              </Alert>
            </>
          )}

          {/* Address display for paper invoices */}
          {(deliveryMethod === 'paper_priority' || deliveryMethod === 'paper_economy') && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t('amili.recipientAddress', 'Vastaanottajan osoite')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2">{invoice?.customer_name || ''}</Typography>
                {invoice?.customer_address_street && (
                  <Typography variant="body2">{invoice.customer_address_street}</Typography>
                )}
                {(invoice?.customer_address_postal_code || invoice?.customer_address_city) && (
                  <Typography variant="body2">
                    {[invoice.customer_address_postal_code, invoice.customer_address_city].filter(Boolean).join(' ')}
                  </Typography>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>{t('Cancel')}</Button>
          <Button
            onClick={handleSendUnified}
            variant="contained"
            disabled={
              sending ||
              buyerNameMissing ||
              (deliveryMethod === 'email' && !sendEmail) ||
              (deliveryMethod === 'email_maventa' && !amiliSendEmail) ||
              (deliveryMethod === 'verkkolasku' && (!verkkolaskuAddress || !verkkolaskuOperator || einvoiceAddressError || operatorCodeError)) ||
              (deliveryNeedsPostalAddress && recipientAddressIncomplete) ||
              senderBankBlocksEinvoice ||
              // Mirror the backend pre-flight so the user is not sent into a failure.
              ((deliveryMethod === 'verkkolasku' || deliveryMethod === 'email_maventa') &&
                attachmentSummary.totalBytes > MAX_MAVENTA_ATTACHMENT_BYTES)
            }
          >
            {sending
              ? <span>{t('Sending...')}</span>
              : <span>{isCreditingDraft ? t('invoices.applyCreditAndSend') : t('Send')}</span>}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark as Paid Dialog */}
      <Dialog
        open={markPaidDialogOpen}
        onClose={() => setMarkPaidDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('Mark as paid')}</DialogTitle>
        <DialogContent>
          {invoice?.status === 'sent' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('invoices.recordPaymentHint')}
            </Typography>
          )}
          {parseFloat(invoice?.amount_paid || 0) > 0 && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('invoices.paidAndOpen', {
                paid: formatCurrency(invoice?.amount_paid),
                remaining: formatCurrency(invoice?.open_balance),
              })}
            </Typography>
          )}
          <DecimalField
            margin="dense"
            fullWidth
            label={t('invoices.paymentAmount')}
            value={markPaidAmount}
            onChange={(value) => {
              setMarkPaidAmount(value);
              setMarkPaidError('');
            }}
            inputProps={{ 'data-testid': 'mark-paid-amount' }}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="mark-paid-method-label">{t('Payment method')}</InputLabel>
            <Select
              labelId="mark-paid-method-label"
              label={t('Payment method')}
              value={markPaidEffectiveMethod}
              onChange={(e) => setMarkPaidMethod(e.target.value)}
              disabled={markPaidCovers || markPaidIsPartial}
              inputProps={{ 'data-testid': 'mark-paid-method' }}
            >
              <MenuItem value="bank_transfer">{t('Bank transfer')}</MenuItem>
              <MenuItem value="cash">{t('Cash payment')}</MenuItem>
              <MenuItem value="card">{t('Card payment')}</MenuItem>
              <MenuItem value="financing">{t('financingPayment')}</MenuItem>
            </Select>
            {markPaidIsPartial && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('invoices.partialBankTransferOnly')}
              </Typography>
            )}
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DatePicker
              label={t('Paid date')}
              value={dateStringToDate(markPaidDate)}
              onChange={(newValue) => setMarkPaidDate(dateToDateString(newValue))}
              format="dd.MM.yyyy"
              slotProps={{
                textField: { margin: 'dense', fullWidth: true },
              }}
            />
          </LocalizationProvider>
          {markPaidError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="mark-paid-error">
              {markPaidError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidDialogOpen(false)} disabled={submittingMarkPaid}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkPaid}
            disabled={submittingMarkPaid || !markPaidDate || markPaidAmount === ''}
          >
            {submittingMarkPaid ? <CircularProgress size={24} /> : t('Mark as paid')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Korjaa lasku */}
      <Dialog
        open={correctDialogOpen}
        onClose={() => setCorrectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('invoices.correctInvoice')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('invoices.correctInvoiceExplanation')}
          </Typography>
          {isAmiliMonitored(invoice) && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="amili-credit-cancel-note">
              {t('amili.creditCancelsCollection')}
            </Alert>
          )}
          <Alert severity="info">
            {t('invoices.correctInvoiceReportsNote')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrectDialogOpen(false)} disabled={correcting}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCorrectInvoice}
            disabled={correcting}
          >
            {correcting ? <CircularProgress size={24} /> : t('invoices.correctInvoice')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merkitse palautetuksi */}
      <Dialog
        open={markRefundedDialogOpen}
        onClose={() => setMarkRefundedDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('invoices.markRefunded')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('invoices.markRefundedExplanation')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkRefundedDialogOpen(false)}>{t('Cancel')}</Button>
          <Button variant="contained" color="success" onClick={handleMarkRefunded}>
            {t('invoices.markRefunded')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hyvitä (cash/card credit note) — apply without a send step. The refund is handed
          over at the till; the document can be printed or emailed afterwards if wanted. */}
      <Dialog
        open={applyCreditDialogOpen}
        onClose={() => setApplyCreditDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('invoices.applyCredit')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {asksRefundMethod
              ? t('invoices.applyCreditCashCardExplanation')
              : t('invoices.applyCreditDeliveredExplanation')}
          </Typography>
          {/* The refund can leave by a different route than the sale came in: the customer is
              not asked to come back to the workshop for a terminal refund. The bucket has to
              follow the money or the card totals stop matching the terminal's own report. */}
          {asksRefundMethod && (
            <FormControl>
              <FormLabel sx={{ fontSize: 14 }}>{t('invoices.refundMethodQuestion')}</FormLabel>
              <RadioGroup
                value={refundMethod}
                onChange={(e) => setRefundMethod(e.target.value)}
              >
                <FormControlLabel
                  value="card"
                  control={<Radio size="small" />}
                  label={t('invoices.refundMethodCard')}
                />
                <FormControlLabel
                  value="cash"
                  control={<Radio size="small" />}
                  label={t('invoices.refundMethodCash')}
                />
                <FormControlLabel
                  value="refund_bank"
                  control={<Radio size="small" />}
                  label={t('invoices.refundMethodBank')}
                />
              </RadioGroup>
            </FormControl>
          )}
          {renderRestockChoice()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyCreditDialogOpen(false)}>{t('Cancel')}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setApplyCreditDialogOpen(false);
              handleApplyCredit(asksRefundMethod ? refundMethod : null, restockParts);
            }}
          >
            {t('invoices.applyCredit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Peru maksumerkintä */}
      <Dialog
        open={unmarkPaidDialogOpen}
        onClose={() => setUnmarkPaidDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('invoices.unmarkPaid')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('invoices.unmarkPaidExplanation')}
          </Typography>
          <TextField
            label={t('invoices.unmarkPaidReason')}
            value={unmarkPaidReason}
            onChange={(e) => setUnmarkPaidReason(e.target.value)}
            margin="dense"
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnmarkPaidDialogOpen(false)} disabled={unmarkingPaid}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleUnmarkPaid}
            disabled={unmarkingPaid}
          >
            {unmarkingPaid ? <CircularProgress size={24} /> : t('invoices.unmarkPaid')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual financing (Rahoitus) payment dialog */}
      <Dialog
        open={financingDialogOpen}
        onClose={() => setFinancingDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('Record financing payment')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('Financing reference (optional)')}
            value={financingReference}
            onChange={(e) => setFinancingReference(e.target.value)}
            placeholder="Resursbank #153531"
            margin="dense"
            fullWidth
            multiline
            minRows={2}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinancingDialogOpen(false)} disabled={markingFinancing}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkFinancingPayment}
            disabled={markingFinancing}
          >
            {markingFinancing ? <CircularProgress size={24} /> : t('financingPayment')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Laskutettu muualla dialog */}
      <Dialog
        open={invoicedElsewhereDialogOpen}
        onClose={() => setInvoicedElsewhereDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid="invoiced-elsewhere-dialog"
      >
        <DialogTitle>{t('invoices.markInvoicedElsewhere')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('invoices.invoicedElsewhereHelp')}
          </Typography>
          <TextField
            label={t('invoices.invoicedElsewhereReferenceOptional')}
            value={invoicedElsewhereReference}
            onChange={(e) => setInvoicedElsewhereReference(e.target.value)}
            placeholder={t('invoices.invoicedElsewhereReferencePlaceholder')}
            margin="dense"
            fullWidth
            multiline
            minRows={2}
            autoFocus
            inputProps={{ maxLength: 500, 'data-testid': 'invoiced-elsewhere-reference-input' }}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DatePicker
              label={t('Paid date')}
              value={dateStringToDate(invoicedElsewhereDate)}
              onChange={(newValue) => setInvoicedElsewhereDate(dateToDateString(newValue))}
              format="dd.MM.yyyy"
              disableFuture
              slotProps={{
                textField: { margin: 'dense', fullWidth: true },
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoicedElsewhereDialogOpen(false)} disabled={markingInvoicedElsewhere}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkInvoicedElsewhere}
            disabled={markingInvoicedElsewhere}
            data-testid="invoiced-elsewhere-confirm"
          >
            {markingInvoicedElsewhere ? <CircularProgress size={24} /> : t('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ohisuoritus Dialog */}
      <Dialog
        open={ohisuoritusDialogOpen}
        onClose={() => setOhisuoritusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('amili.externalPaymentTitle', 'Merkitse ohisuoritus')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {ohisuoritusConflict
              ? t('amili.conflictPaymentDescription')
              : t('amili.externalPaymentDescription', 'Asiakas on maksanut suoraan yrityksen tilille. Ilmoita maksu Amilille perinnän pysäyttämiseksi.')}
          </Typography>
          {ohisuoritusConflict ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('amili.openClaimLine', { amount: formatCurrency(ohisuoritusConflictClaim) })}
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('amili.openBalanceLine', { amount: formatCurrency(ohisuoritusOpenBalance) })}
              </Typography>
              <DecimalField
                margin="dense"
                fullWidth
                label={t('amili.paymentAmount', 'Maksun summa')}
                value={ohisuoritusAmount}
                onChange={(value) => {
                  setOhisuoritusAmount(value);
                  setOhisuoritusError('');
                }}
                inputProps={{ 'data-testid': 'ohisuoritus-amount' }}
                sx={{ mb: 2 }}
              />
            </>
          )}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DatePicker
              label={t('amili.paymentDate', 'Maksupäivä')}
              value={dateStringToDate(ohisuoritusDate)}
              onChange={(newValue) => setOhisuoritusDate(dateToDateString(newValue))}
              format="dd.MM.yyyy"
              disableFuture
              slotProps={{
                textField: { margin: 'dense', fullWidth: true },
              }}
            />
          </LocalizationProvider>
          {!ohisuoritusConflict && ohisuoritusPartialRefused && (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="ohisuoritus-partial-warning">
              {t('invoices.errorAmiliPartialNotSupported')}
            </Alert>
          )}
          {!ohisuoritusConflict && ohisuoritusOverBalance && (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="ohisuoritus-over-warning">
              {t('invoices.errorPaymentExceedsOpenBalance')}
            </Alert>
          )}
          {ohisuoritusValid && ohisuoritusMonitored && (
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 500 }} data-testid="ohisuoritus-summary">
              {t('amili.willReportLine', {
                amount: formatCurrency(ohisuoritusConflict ? ohisuoritusConflictClaim : ohisuoritusOpenBalance),
                date: formatDate(ohisuoritusDate),
              })}
            </Typography>
          )}
          {ohisuoritusError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="ohisuoritus-error">
              {ohisuoritusError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOhisuoritusDialogOpen(false)} disabled={submittingOhisuoritus}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleOhisuoritus}
            disabled={submittingOhisuoritus || !ohisuoritusValid}
            data-testid="ohisuoritus-submit"
          >
            {submittingOhisuoritus ? <CircularProgress size={24} /> : t('amili.reportPayment', 'Ilmoita maksu')}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Reverse a recorded payment */}
      <Dialog
        open={Boolean(reversePaymentTarget)}
        onClose={() => setReversePaymentTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('invoices.reversePayment')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {reversePaymentTarget
              && t('invoices.reversePaymentConfirm', {
                amount: formatCurrency(reversePaymentTarget.amount),
                date: formatDate(reversePaymentTarget.payment_date),
              })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReversePaymentTarget(null)} disabled={reversingPayment}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" color="warning" onClick={handleReversePayment} disabled={reversingPayment}>
            {reversingPayment ? <CircularProgress size={24} /> : t('invoices.reversePayment')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Receipt Dialog */}
      <Dialog open={sendReceiptDialogOpen} onClose={() => setSendReceiptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('Send receipt to customer')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('Email')}
            type="email"
            fullWidth
            value={sendReceiptEmail}
            onChange={(e) => setSendReceiptEmail(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendReceiptDialogOpen(false)}>{t('Cancel')}</Button>
          <Button
            onClick={handleSendReceipt}
            variant="contained"
            disabled={sendingReceipt || !sendReceiptEmail}
            startIcon={sendingReceipt ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {t('Send receipt')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Combined Receipt Dialog */}
      <Dialog open={sendCombinedReceiptDialogOpen} onClose={() => setSendCombinedReceiptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('Send combined receipt to customer')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('Email')}
            type="email"
            fullWidth
            value={sendCombinedReceiptEmail}
            onChange={(e) => setSendCombinedReceiptEmail(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendCombinedReceiptDialogOpen(false)}>{t('Cancel')}</Button>
          <Button
            onClick={handleSendCombinedReceipt}
            variant="contained"
            disabled={sendingCombinedReceipt || !sendCombinedReceiptEmail}
            startIcon={sendingCombinedReceipt ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {t('Send combined receipt')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Editing Dialogs */}
      <EditInvoiceLineDialog
        open={editLineDialogOpen}
        handleClose={() => {
          setEditLineDialogOpen(false);
          setSelectedLine(null);
        }}
        line={selectedLine}
        invoiceId={invoice?.id}
        isCreditNote={invoice?.invoice_type === 'INV02'}
        onUpdate={handleInvoiceUpdate}
      />

      <AddInvoiceLineDialog
        open={addLineDialogOpen}
        handleClose={() => setAddLineDialogOpen(false)}
        invoiceId={invoice?.id}
        isCreditNote={invoice?.invoice_type === 'INV02'}
        onUpdate={handleInvoiceUpdate}
        defaultDiscountPercent={invoice?.customer_discount_percent}
      />

      <EditInvoiceHeaderDialog
        open={editHeaderDialogOpen}
        handleClose={() => {
          setEditHeaderDialogOpen(false);
          setEditHeaderFocusField(null);
        }}
        invoice={invoice}
        onUpdate={handleInvoiceUpdate}
        focusField={editHeaderFocusField}
        setSnackbar={setSnackbar}
      />

      <EditInvoiceInsuranceDialog
        open={insuranceDialogOpen}
        handleClose={() => setInsuranceDialogOpen(false)}
        invoice={invoice}
        onUpdate={handleInvoiceUpdate}
        setSnackbar={setSnackbar}
        currentOrganization={currentOrganization}
      />

      {/* Edit the invoice's customer, then reflect changes back onto the invoice */}
      {editCustomerData && (
        <CustomerDialog
          t={t}
          open={editCustomerDialogOpen}
          handleClose={() => setEditCustomerDialogOpen(false)}
          customer={editCustomerData}
          updateCustomer={handleCustomerSaved}
          addCreatedCustomer={() => {}}
        />
      )}

      {/* Apply Discount Dialog */}
      <Dialog
        open={applyDiscountDialogOpen}
        onClose={() => {
          setApplyDiscountDialogOpen(false);
          setDiscountPercent('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalOfferIcon color="primary" />
            {t('Apply discount to all lines')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            {t('This will set the same discount percentage on every invoice line. The discount will be shown on the invoice and can be changed later by editing individual lines.')}
          </DialogContentText>

          <TextField
            autoFocus
            label={t('Discount percentage')}
            type="number"
            fullWidth
            value={discountPercent}
            onChange={(e) => setDiscountPercent(e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: { min: 0.01, max: 100, step: 0.01 },
            }}
            helperText={t('Enter a value between 0.01 and 100')}
          />

        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApplyDiscountDialogOpen(false);
              setDiscountPercent('');
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleApplyDiscount}
            variant="contained"
            disabled={applyingDiscount || !discountPercent || parseFloat(discountPercent) <= 0 || parseFloat(discountPercent) > 100}
            startIcon={applyingDiscount ? <CircularProgress size={16} /> : <LocalOfferIcon />}
          >
            {applyingDiscount ? <span>{t('Applying...')}</span> : <span>{t('Apply discount')}</span>}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Round Total Dialog */}
      <Dialog
        open={roundTotalDialogOpen}
        onClose={() => {
          setRoundTotalDialogOpen(false);
          setDesiredTotal('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalOfferIcon color="primary" />
            {t('Round invoice total')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            {t('roundTotalDescription')}
          </DialogContentText>

          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('Current total')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatCurrency(invoice?.total_incl_vat)}
            </Typography>
          </Box>

          <DecimalField
            autoFocus
            label={t('Desired final price')}
            fullWidth
            value={desiredTotal}
            onChange={setDesiredTotal}
            InputProps={{
              endAdornment: <InputAdornment position="end">€</InputAdornment>,
            }}
          />

          {desiredTotal && parseFloat(desiredTotal) > 0 && parseFloat(desiredTotal) < parseFloat(invoice?.total_incl_vat) && (() => {
            const diff = parseFloat(invoice?.total_incl_vat) - parseFloat(desiredTotal);
            const vatRate = getDominantVatRate();
            return (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.light' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('Difference')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  -{formatCurrency(diff)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('VAT')} {vatRate.toFixed(1).replace('.', ',')} %
                </Typography>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRoundTotalDialogOpen(false);
              setDesiredTotal('');
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleRoundTotal}
            variant="contained"
            disabled={
              roundingTotal ||
              !desiredTotal ||
              parseFloat(desiredTotal) <= 0 ||
              parseFloat(desiredTotal) >= parseFloat(invoice?.total_incl_vat)
            }
            startIcon={roundingTotal ? <CircularProgress size={16} /> : <LocalOfferIcon />}
          >
            {roundingTotal ? <span>{t('Rounding...')}</span> : <span>{t('Round')}</span>}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Event Log Dialog */}
      <InvoiceEventLogDialog
        open={eventLogDialogOpen}
        handleClose={() => setEventLogDialogOpen(false)}
        invoice={invoice}
      />

      {/* Maksumuistutus */}
      <PaymentReminderDialog
        open={reminderDialogOpen}
        invoice={invoice}
        onClose={() => setReminderDialogOpen(false)}
        onSent={() => fetchInvoice()}
        onNotify={(message, severity) => setSnackbar({ open: true, message, severity })}
      />

      {/* Split Payment Dialog */}
      <SplitPaymentDialog
        open={splitPaymentDialogOpen}
        handleClose={() => setSplitPaymentDialogOpen(false)}
        invoiceId={invoice?.id}
        invoiceTotal={invoice?.total_incl_vat}
        terminals={terminals}
        paymentTerminalEnabled={paymentTerminalEnabled}
        existingLegs={paymentLegs}
        onPartial={fetchInvoice}
        onSuccess={(data) => {
          setSplitPaymentDialogOpen(false);
          // A failed card capture still leaves a valid split: the card invoice is a draft
          // waiting to be paid. Send the user there to retry rather than to the bank leg.
          const cardPayment = data.card_payment;
          if (cardPayment && cardPayment.success === false) {
            if (cardPayment.invoice_id) {
              navigate(`/invoices/${cardPayment.invoice_id}`);
            }
            setSnackbar({
              open: true,
              message: t('splitPayment.cardCaptureFailed', {
                error: cardPayment.result_description || cardPayment.error || '',
              }),
              severity: 'warning',
            });
            return;
          }
          // Every recorded leg was given back: an ordinary draft again, same page.
          if (data.legs_refunded) {
            fetchInvoice();
            setSnackbar({ open: true, message: t('splitPayment.legsRefunded'), severity: 'success' });
            return;
          }
          // Paid in full at the counter: one invoice, tender legs, same page.
          if (data.settled_with_legs) {
            fetchInvoice();
            setSnackbar({ open: true, message: t('splitPayment.successSingleInvoice'), severity: 'success' });
            return;
          }
          const bankInvoice = data.invoices?.find((i) => i.payment_method === 'bank_transfer');
          const targetId = bankInvoice ? bankInvoice.id : data.invoices?.[0]?.id;
          if (targetId) {
            navigate(`/invoices/${targetId}`);
          }
          setSnackbar({ open: true, message: t('splitPayment.success'), severity: 'success' });
        }}
      />

      {/* Mark as Sent Dialog */}
      <Dialog open={markAsSentDialogOpen} onClose={() => setMarkAsSentDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('Mark as sent')}</DialogTitle>
        <DialogContent>
          {buyerNameWarning}
          <DialogContentText>
            {t('markAsSentConfirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkAsSentDialogOpen(false)} disabled={markingAsSent}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleMarkAsSent}
            disabled={markingAsSent || buyerNameMissing}
            startIcon={markingAsSent ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {markingAsSent ? t('Processing...') : t('Mark as sent')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for cancel/rollback actions */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmDialogAction === 'cancel' ? t('Cancel invoice?') : t('Rollback to draft?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialogAction === 'cancel'
              ? t('Are you sure you want to cancel this invoice? This action cannot be undone.')
              : t('Are you sure you want to rollback this invoice to draft? The invoice will need to be sent again.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} disabled={confirmDialogLoading}>
            {t('No, go back')}
          </Button>
          <Button
            variant="contained"
            color={confirmDialogAction === 'cancel' ? 'error' : 'primary'}
            onClick={handleConfirmDialogConfirm}
            disabled={confirmDialogLoading}
          >
            {confirmDialogLoading ? (
              <CircularProgress size={24} />
            ) : confirmDialogAction === 'cancel' ? (
              t('Yes, cancel invoice')
            ) : (
              t('Yes, rollback to draft')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Terminal select dialog (when multiple terminals) */}
      <Dialog
        open={terminalSelectOpen}
        onClose={() => setTerminalSelectOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('terminal.selectTerminal')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {terminals.map((terminal) => (
              <Button
                key={terminal.id}
                variant="outlined"
                fullWidth
                onClick={() => handleTerminalPayment(terminal.id)}
                startIcon={<CreditCardIcon />}
              >
                {terminal.name || terminal.terminal_id}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminalSelectOpen(false)}>{t('Cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Refund confirmation dialog */}
      <Dialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{hasTenderLegs ? t('terminal.refundLeggedTitle') : t('terminal.refundTitle')}</DialogTitle>
        <DialogContent>
          {hasTenderLegs ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }} data-testid="invoice-refund-legs">
              <Typography variant="body2" color="text.secondary">{t('terminal.refundLeggedInfo')}</Typography>
              <Box>
                {(invoice?.payment_entries || []).map((leg) => (
                  <Box
                    key={leg.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.5, opacity: leg.refunded_at ? 0.5 : 1 }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {paymentMethodLabels[leg.payment_method] || leg.payment_method}
                      {leg.card_number_masked ? ` · ${formatMaskedCard(leg.card_number_masked)}` : ''}
                      {!leg.terminal_transaction ? ` · ${t('terminal.refundByHand')}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(leg.amount)}
                    </Typography>
                    {leg.refunded_at && (
                      <Chip size="small" color="success" label={t('terminal.legRefunded')} />
                    )}
                  </Box>
                ))}
              </Box>
              {cashToReturnOnRefund > 0 && (
                <Alert severity="info">
                  {t('terminal.refundCashByHand', { amount: formatCurrency(cashToReturnOnRefund) })}
                </Alert>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={refundCustomerNotPresent}
                    onChange={(e) => setRefundCustomerNotPresent(e.target.checked)}
                  />
                }
                label={t('terminal.refundCustomerNotPresent')}
              />
              <Alert severity="warning">{t('terminal.refundWarning')}</Alert>
            </Box>
          ) : (() => {
            const purchaseTxn = invoice?.terminal_transactions?.find(
              t => t.transaction_type === 'purchase' && t.result_code === 'SUCCESS'
            );
            const amountEur = purchaseTxn ? purchaseTxn.amount_eur : invoice?.total_incl_vat;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Typography>
                  {t('terminal.refundAmount')}: <strong>{parseFloat(amountEur).toFixed(2)} €</strong>
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={refundCustomerNotPresent}
                      onChange={(e) => setRefundCustomerNotPresent(e.target.checked)}
                    />
                  }
                  label={t('terminal.refundCustomerNotPresent')}
                />
                {refundCustomerNotPresent && (
                  <Typography variant="body2" color="text.secondary">
                    {t('terminal.refundCustomerNotPresentHelp')}
                  </Typography>
                )}
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('terminal.refundWarning')}
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>{t('Cancel')}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRefundConfirm}
          >
            {t('terminal.refundConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund terminal select dialog (when multiple terminals) */}
      <Dialog
        open={refundTerminalSelectOpen}
        onClose={() => setRefundTerminalSelectOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('terminal.selectTerminal')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {terminals.map((terminal) => (
              <Button
                key={terminal.id}
                variant="outlined"
                fullWidth
                onClick={() => handleTerminalRefund(terminal.id)}
                startIcon={<UndoIcon />}
              >
                {terminal.name || terminal.terminal_id}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundTerminalSelectOpen(false)}>{t('Cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Svea financing dialog */}
      <Dialog open={sveaDialogOpen} onClose={() => setSveaDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('svea.financingCreated', 'Rahoitushakemus luotu')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('svea.sendLinkToCustomer', 'Lähetä rahoituslinkki asiakkaalle:')}
          </Typography>
          <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
              {invoice?.svea_payment?.customer_url || invoice?.svea_payment?.payment_url || ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                navigator.clipboard.writeText(
                  invoice?.svea_payment?.customer_url || invoice?.svea_payment?.payment_url || ''
                );
                setSnackbar({ open: true, message: t('svea.linkCopied', 'Linkki kopioitu'), severity: 'success' });
              }}
              fullWidth
            >
              {t('svea.copyLink', 'Kopioi linkki')}
            </Button>
            {invoice?.customer_email && (
              <Button
                variant="outlined"
                startIcon={sveaSending === 'email' ? <CircularProgress size={16} /> : <SendIcon />}
                disabled={!!sveaSending}
                onClick={async () => {
                  setSveaSending('email');
                  try {
                    await axios.post(`/api/invoices/${id}/svea-financing-send/`, {
                      send_email: true,
                      email: invoice.customer_email,
                    });
                    setSnackbar({ open: true, message: t('svea.emailSent', 'Sähköposti lähetetty'), severity: 'success' });
                  } catch (err) {
                    setSnackbar({ open: true, message: err.response?.data?.error || t('Error'), severity: 'error' });
                  } finally {
                    setSveaSending(null);
                  }
                }}
                fullWidth
              >
                {t('svea.sendEmail', 'Lähetä sähköpostilla')} ({invoice.customer_email})
              </Button>
            )}
            {invoice?.customer_phone && (
              <Button
                variant="outlined"
                startIcon={sveaSending === 'sms' ? <CircularProgress size={16} /> : null}
                disabled={!!sveaSending}
                onClick={async () => {
                  setSveaSending('sms');
                  try {
                    await axios.post(`/api/invoices/${id}/svea-financing-send/`, {
                      send_sms: true,
                      phone: invoice.customer_phone,
                    });
                    setSnackbar({ open: true, message: t('svea.smsSent', 'Tekstiviesti lähetetty'), severity: 'success' });
                  } catch (err) {
                    setSnackbar({ open: true, message: err.response?.data?.error || t('Error'), severity: 'error' });
                  } finally {
                    setSveaSending(null);
                  }
                }}
                fullWidth
              >
                {t('svea.sendSms', 'Lähetä tekstiviestillä')} ({invoice.customer_phone})
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSveaDialogOpen(false)}>{t('Close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Svea S2S credit decision dialog */}
      <Dialog open={sveaS2sDialogOpen} onClose={() => setSveaS2sDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('svea.creditDecision', 'Svea rahoituspäätös')}</DialogTitle>
        <DialogContent>
          {/* Financing type selection */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('svea.selectFinancingType', 'Valitse rahoitusmuoto')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={sveaS2sPaymentMethod === 'FI70' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => { setSveaS2sPaymentMethod('FI70'); setSveaS2sSelectedPlan(null); }}
              >
                {t('svea.sveaLasku', 'Svea Lasku')}
              </Button>
              <Button
                variant={sveaS2sPaymentMethod === 'FI71' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setSveaS2sPaymentMethod('FI71');
                  if (sveaS2sPlans.length === 0 && !sveaS2sPlansLoading) handleFetchPlans();
                }}
              >
                {t('svea.sveaEramaksu', 'Svea Erämaksu')}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {sveaS2sPaymentMethod === 'FI70'
                ? t('svea.sveaLaskuDesc', 'Asiakas saa laskun Svealta')
                : t('svea.sveaEramaksuDesc', 'Asiakas maksaa erissä')}
            </Typography>
          </Box>

          {/* Payment plans (only for FI71) */}
          {sveaS2sPaymentMethod === 'FI71' && (
            <Box sx={{ mb: 2 }}>
              {sveaS2sPlansLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">{t('svea.fetchingPlans', 'Haetaan maksusuunnitelmia...')}</Typography>
                </Box>
              ) : sveaS2sPlans.length === 0 ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {t('svea.noPlansAvailable', 'Maksusuunnitelmia ei ole saatavilla tälle summalle')}
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    {t('svea.selectPlan', 'Valitse maksusuunnitelma')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 200, overflowY: 'auto' }}>
                    {sveaS2sPlans.map((plan) => (
                      <Box
                        key={plan.CampaignCode}
                        onClick={() => setSveaS2sSelectedPlan(plan)}
                        sx={{
                          p: 1.5, borderRadius: 1, cursor: 'pointer',
                          border: '1px solid',
                          borderColor: sveaS2sSelectedPlan?.CampaignCode === plan.CampaignCode ? 'primary.main' : 'divider',
                          bgcolor: sveaS2sSelectedPlan?.CampaignCode === plan.CampaignCode ? 'primary.50' : 'transparent',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {plan.ContractLengthInMonths} {t('svea.months', 'kk')} — {plan.MonthlyAnnuity} €{t('svea.perMonth', '/kk')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('svea.interestRate', 'Korko')}: {plan.InterestRatePercent}%
                          </Typography>
                        </Box>
                        {plan.Description && (
                          <Typography variant="caption" color="text.secondary">{plan.Description}</Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Henkilötunnus input */}
          <TextField
            fullWidth
            label={t('svea.enterHetu', 'Asiakkaan henkilötunnus')}
            placeholder={t('svea.hetuPlaceholder', 'ppkkvv-XXXX')}
            value={sveaS2sHetu}
            onChange={(e) => setSveaS2sHetu(e.target.value)}
            sx={{ mb: 2 }}
            autoComplete="off"
            inputProps={{ autoComplete: 'off' }}
          />

          {/* Result messages */}
          {sveaS2sResult?.success && (
            <Alert severity="success" sx={{ mb: 1 }}>
              {t('svea.decisionApproved', 'Rahoituspäätös hyväksytty!')}
            </Alert>
          )}
          {sveaS2sResult?.denied && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {t('svea.decisionDenied', 'Rahoituspäätös hylätty. Luottoa ei voida myöntää.')}
              {sveaS2sResult.message && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{sveaS2sResult.message}</Typography>
              )}
            </Alert>
          )}
          {sveaS2sResult?.error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {sveaS2sResult.message || t('svea.decisionError', 'Rahoituspäätöksen haussa tapahtui virhe')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSveaS2sDialogOpen(false)}>{t('Cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleS2sSubmit}
            disabled={sveaS2sLoading || !sveaS2sHetu || (sveaS2sPaymentMethod === 'FI71' && !sveaS2sSelectedPlan)}
            startIcon={sveaS2sLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('svea.requestDecision', 'Hae rahoituspäätös')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Svea refund confirmation dialog */}
      <FinancingRefundDialog
        open={sveaRefundDialogOpen}
        onClose={() => setSveaRefundDialogOpen(false)}
        onConfirm={handleSveaRefund}
        loading={sveaRefundLoading}
        title={t('svea.refundTitle')}
        amount={invoice?.svea_payment?.amount || invoice?.total_incl_vat}
        warning={t('svea.refundWarning')}
      />

      {/* Resurs: send the SMS link and wait for the customer */}
      <ResursFinancingDialog
        open={resursDialogOpen}
        onClose={() => setResursDialogOpen(false)}
        invoice={invoice}
        onInvoiceUpdated={(data) => setInvoice(data)}
        onRefresh={refreshInvoiceQuietly}
        onNotify={(message, severity) => setSnackbar({ open: true, message, severity })}
      />

      {/* Resurs refund confirmation dialog */}
      <FinancingRefundDialog
        open={resursRefundDialogOpen}
        onClose={() => setResursRefundDialogOpen(false)}
        onConfirm={handleResursRefund}
        loading={resursRefundLoading}
        title={t('resurs.refundTitle')}
        amount={invoice?.resurs_payment?.amount || invoice?.total_incl_vat}
        warning={t('resurs.refundWarning')}
        testId="resurs-refund-dialog"
      />

      {/* Svea bank transfer info dialog (shown after refund-after-settlement) */}
      <Dialog open={!!sveaBankTransfer} onClose={() => setSveaBankTransfer(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('svea.bankTransferRequired')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('svea.bankTransferInfo')}
          </Alert>
          {sveaBankTransfer && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('svea.bankTransferRecipient')}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{sveaBankTransfer.recipient}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('svea.bankTransferIban')}</Typography>
                <Typography variant="body1" sx={{ ...numFont }}>{sveaBankTransfer.iban}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('svea.bankTransferAmount')}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{sveaBankTransfer.amount} €</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('svea.bankTransferReference')}</Typography>
                <Typography variant="body1" sx={{ ...numFont, fontWeight: 500 }}>{sveaBankTransfer.reference}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSveaBankTransfer(null)}>{t('Close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default withTranslation()(InvoiceDetailView);
